import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spin, Empty, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { LotDetailContent } from './LotDetail';
import { useAuth } from '../contexts/AuthContext';
import ImageSearchButton from '../components/ImageSearchButton';
import RecommendSection from '../components/RecommendSection';
import dayjs from 'dayjs';
import { fmtCST } from '../utils/time';
import { thumbCard } from '../utils/img';
import { LOT_CATEGORIES, LOT_CATEGORIES_WITH_ALL } from '../config/lotCategories';
import useBackRestore, { useScrollRestore } from '../hooks/useBackRestore';

const GOLD = '#B8892A';
const BD = '#D8D2C8';
const BD2 = '#EAE6DF';
const BD3 = '#F0EDE8';
const BG = '#FAF8F5';
const BG2 = '#F4F1EC';
const BG3 = '#EDEAE3';
const INK = '#1A1612';
const INK2 = '#3D3830';
const INK3 = '#7A7268';
const INK4 = '#B0A898';
const INK5 = '#D0C5B8';
const RED = '#9B2335';
const GRN = '#2A6645';
const BLUE = '#2B5CE6';
const JPY_CNY_FALLBACK = 0.043; // 仅在动态汇率加载失败时使用

// 一级分类统一走共享源 lotCategories.js(顶部tab/筛选/SessionLots 一套,改一处全同步)
const CATEGORIES = LOT_CATEGORIES_WITH_ALL;   // 顶部快捷 tab(含「全部」)
const FILTER_CATS = LOT_CATEGORIES;           // 筛选面板品类(多选,无「全部」)
// 筛选面板 — 成色等级
const GRADES = ['SA', 'S', 'A', 'AB', 'B+', 'B', 'B-', 'BC', 'C', 'D'];
// 空筛选默认值
const EMPTY_FILTER = {
  priceMin: '', priceMax: '',
  startMin: '', startMax: '',
  buynowMin: '', buynowMax: '',
  categories: [],
  grades: [],
  brands: [],
};

const SESSION_TYPE_MAP = {
  watch: { labelKey: 'browse.watches', emoji: '⌚', color: BLUE, bg: 'rgba(43,92,230,.1)' },
  jewelry: { labelKey: 'browse.jewelry', emoji: '💍', color: '#6741D9', bg: 'rgba(103,65,217,.1)' },
  jewel: { labelKey: 'browse.jewelry', emoji: '💍', color: '#6741D9', bg: 'rgba(103,65,217,.1)' },
  bag: { labelKey: 'browse.bags', emoji: '👜', color: GOLD, bg: 'rgba(200,146,42,.1)' },
  mixed: { labelKey: 'browse.filterCatMixed', emoji: '📦', color: INK3, bg: 'rgba(26,22,18,.06)' },
};

const STATUS_MAP = {
  live: { dot: RED, labelKey: 'browse.statusLive', bg: 'rgba(155,35,53,.08)', color: RED },
  preview: { dot: GOLD, labelKey: 'browse.statusPreview', bg: 'rgba(184,137,42,.12)', color: GOLD },
  upcoming: { dot: '#9CA3AF', labelKey: 'browse.statusUpcoming', bg: BG3, color: INK4 },
  ended: { dot: GRN, labelKey: 'browse.statusEnded', bg: 'rgba(42,102,69,.08)', color: GRN },
  done: { dot: GRN, labelKey: 'browse.statusEnded', bg: 'rgba(42,102,69,.08)', color: GRN },
  closed: { dot: INK4, labelKey: 'browse.statusEnded', bg: BG3, color: INK4 },
};

function formatJPY(val) {
  if (val == null) return '--';
  const n = Number(val);
  return n >= 10000 ? `${(n / 10000).toFixed(1)}万` : `¥${n.toLocaleString()}`;
}

function useCountdown(endAt) {
  const [text, setText] = useState('');
  const [longText, setLongText] = useState('');
  const [urgent, setUrgent] = useState(false);
  useEffect(() => {
    if (!endAt) { setText(''); setLongText(''); return; }
    const update = () => {
      const diff = dayjs(endAt).diff(dayjs(), 'second');
      if (diff <= 0) { setText('已结束'); setLongText('已结束'); setUrgent(false); return; }
      setUrgent(diff < 3 * 3600);
      const d = Math.floor(diff / 86400), h = Math.floor((diff % 86400) / 3600),
        m = Math.floor((diff % 3600) / 60), s = diff % 60;
      // 图片角标：紧凑英文格式
      if (d > 0) setText(`${d}天${h}h`);
      else if (h > 0) setText(`${h}h${String(m).padStart(2, '0')}m`);
      else setText(`${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
      // 卡片 body：完整中文格式
      if (d > 0) setLongText(`${d}天${h}小时${m}分钟${s}秒`);
      else if (h > 0) setLongText(`${h}小时${m}分钟${s}秒`);
      else setLongText(`${m}分钟${s}秒`);
    };
    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [endAt]);
  return { text, longText, urgent };
}

/* ── SessionCard — 场次卡片（参照竞品"会场"页面风格） ── */
function SessionCard({ session, onSelect, allClosedFn, isSessionLiveFn }) {
  const { longText, urgent } = useCountdown(session.end_at);
  const ended = allClosedFn(session);
  const isLive = isSessionLiveFn(session);
  const borderColor = ended ? INK4 : isLive ? '#2B5CE6' : GOLD;
  // 状态标签
  const statusBadge = ended
    ? { text: '已结束', bg: BG3, color: INK4 }
    : isLive
      ? { text: '正在进行', bg: 'rgba(155,35,53,.08)', color: RED }
      : { text: '即将开始', bg: 'rgba(184,137,42,.12)', color: GOLD };
  // 新品标签
  const isNewToday = session.created_at && dayjs().diff(dayjs(session.created_at), 'hour') < 24;

  return (
    <div
      onClick={() => onSelect(session)}
      style={{
        background: '#fff', borderRadius: 10, cursor: 'pointer',
        border: `1px solid ${BD2}`, borderLeft: `4px solid ${borderColor}`,
        padding: '16px 16px 14px', marginBottom: 12,
        transition: 'box-shadow .15s, border-color .15s',
        position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(26,20,16,.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* 右上角状态标签 */}
      {isNewToday && !ended && (
        <span style={{
          position: 'absolute', top: 10, right: 12,
          fontSize: 10, padding: '2px 8px', borderRadius: 3,
          background: '#2A6645', color: '#fff', fontWeight: 600,
        }}>今日上新</span>
      )}

      {/* 标题行 */}
      <div style={{ fontSize: 16, fontWeight: 600, color: ended ? INK4 : '#1A4F8C', marginBottom: 10, lineHeight: 1.3, paddingRight: isNewToday ? 70 : 0 }}>
        {session.house_name} {session.name}
      </div>

      <div style={{ borderTop: `1px solid ${BD2}`, paddingTop: 10 }}>
        {/* 剩余时间 */}
        {!ended && longText && (
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: INK2 }}>剩余时间：</span>
            <span style={{
              fontSize: 14, fontWeight: 700,
              color: urgent ? RED : '#C92A2A',
            }}>{longText}</span>
          </div>
        )}

        {/* 商品件数 */}
        <div style={{ fontSize: 13, color: INK3, marginBottom: 4 }}>
          商品件数：<span style={{ fontFamily: 'var(--mono,monospace)', fontWeight: 500, color: INK }}>{(session.lot_count || 0).toLocaleString()}</span>
        </div>

        {/* 结束时间 */}
        {session.end_at && (
          <div style={{ fontSize: 12, color: INK4, marginBottom: 4 }}>
            截止时间：{new Date(session.end_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })}（北京时间）
          </div>
        )}

        {/* 状态徽章 */}
        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 3,
            background: statusBadge.bg, color: statusBadge.color, fontWeight: 600,
          }}>● {statusBadge.text}</span>
        </div>
      </div>
    </div>
  );
}

/* -- Lot Detail Dialog -- */
function LotDetailDialog({ lotId, titleJa, onClose, onBidPlaced }) {
  const { t } = useTranslation();
  const overlayRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ESC to close
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!lotId) return null;

  /* ── Mobile: bottom-sheet ── */
  if (isMobile) {
    return (
      <div
        ref={overlayRef}
        onClick={e => { if (e.target === overlayRef.current) onClose(); }}
        style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(26,22,18,.55)', backdropFilter: 'blur(3px)' }}
      >
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: '#FAF8F5', borderRadius: '16px 16px 0 0',
          height: '87vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 -8px 40px rgba(26,22,18,.22)', overflow: 'hidden',
        }}>
          {/* Header: handle + title + close */}
          <div style={{ flexShrink: 0, background: '#fff', borderBottom: '1px solid #EAE6DF' }}>
            {/* Drag handle */}
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 6 }}>
              <div style={{ width: 40, height: 4, borderRadius: 2, background: '#C8C0B4' }} />
            </div>
            {/* Title row */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px 12px', gap: 10 }}>
              <div style={{ flex: 1, fontSize: 14, fontWeight: 600, color: '#1A1410', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {titleJa || t('lotDetail.title')}
              </div>
              <button
                onClick={onClose}
                style={{
                  flexShrink: 0, width: 36, height: 36, border: 'none', borderRadius: '50%',
                  background: '#1A1410', cursor: 'pointer', fontSize: 18, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >✕</button>
            </div>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <LotDetailContent lotId={lotId} isDialog={true} onBidPlaced={onBidPlaced} />
          </div>
        </div>
      </div>
    );
  }

  /* ── Desktop: centered modal ── */
  return (
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(26,22,18,.55)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '20px 16px',
      }}
    >
      <div style={{
        background: '#FAF8F5', borderRadius: 10, width: '100%', maxWidth: 1100,
        height: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(26,22,18,.28)', overflow: 'hidden',
      }}>
        {/* Close button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 12px', flexShrink: 0, borderBottom: '1px solid #EAE6DF' }}>
          <button
            onClick={onClose}
            style={{ width: 32, height: 32, border: '1px solid #D8D2C8', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 18, color: '#7A7268', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >✕</button>
        </div>
        {/* Full detail content — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <LotDetailContent lotId={lotId} isDialog={true} onBidPlaced={onBidPlaced} />
        </div>
      </div>
    </div>
  );
}

/* -- 结拍判定: DB status OR 客户端 end_at 过期 -- */
const isEffectivelyEnded = (lot) =>
  lot.status === 'ended' || lot.status === 'closed' ||
  (lot.end_at && dayjs(lot.end_at).isBefore(dayjs()));

/* -- Lot Card (grid mode) -- */
function LotCard({ lot, isWatchlisted, isBidding, onToggleWatchlist, onOpenDetail, jpyCnyRate = JPY_CNY_FALLBACK }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { text: countdown, longText: countdownLong, urgent } = useCountdown(lot.end_at);
  let images = [];
  try { images = typeof lot.image_urls === 'string' ? JSON.parse(lot.image_urls) : (lot.image_urls || []); } catch { }
  const imgSrc = images[0] || null;
  const catObj = CATEGORIES.find(c => c.value === lot.category);
  const catLabel = catObj ? t(catObj.labelKey) : '';
  const emoji = catLabel?.charAt(0) === '⌚' || catLabel?.charAt(0) === '💍' || catLabel?.charAt(0) === '👜' ? catLabel.charAt(0) : '🎁';
  const currentPrice = lot.current_price_jpy || lot.start_price_jpy || 0;
  const cnyCurrent = Math.round(currentPrice * jpyCnyRate);

  return (
    <div
      onClick={() => onOpenDetail ? onOpenDetail(lot) : navigate(`/lots/${lot.id}`)}
      style={{
        background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8,
        overflow: 'hidden', cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(26,20,16,.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = GOLD; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = BD2; }}
    >
      {/* Image */}
      <div style={{ height: 130, background: BG3, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {imgSrc ? (
          <img src={thumbCard(imgSrc)} alt="" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span style={{ fontSize: 44 }}>{emoji}</span>
        )}
        {/* Bidding / result badge */}
        {isBidding && isEffectivelyEnded(lot) ? (
          // 结拍后：显示竞得 / 未竞得
          lot.mandate_won ? (
            <div style={{
              position: 'absolute', top: 7, left: 7, fontSize: 9, padding: '2px 6px',
              background: 'rgba(34,139,34,.88)', color: '#fff', borderRadius: 3, fontWeight: 700,
              pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 3,
              boxShadow: '0 1px 4px rgba(34,139,34,.4)',
            }}>🏆 {t('browse.won')}</div>
          ) : (
            <div style={{
              position: 'absolute', top: 7, left: 7, fontSize: 9, padding: '2px 6px',
              background: 'rgba(100,100,100,.75)', color: '#fff', borderRadius: 3, fontWeight: 600,
              pointerEvents: 'none',
            }}>{t('browse.lost')}</div>
          )
        ) : isBidding ? (
          // 未结拍：竞拍中
          <div style={{
            position: 'absolute', top: 7, left: 7, fontSize: 9, padding: '2px 6px',
            background: RED, color: '#fff', borderRadius: 3, fontWeight: 700,
            pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 3,
            boxShadow: '0 1px 4px rgba(155,35,53,.4)',
          }}>
            <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: '#fff', animation: 'pulse 1.2s ease-in-out infinite' }} />
            {t('browse.biddingNow')}
          </div>
        ) : null}
        {/* Featured badge */}
        {!isBidding && !!lot.is_featured && (
          <div style={{
            position: 'absolute', top: 7, left: 7, fontSize: 9, padding: '2px 5px',
            background: GOLD, color: '#fff', borderRadius: 3, fontWeight: 600, pointerEvents: 'none',
          }}>{t('browse.featured')}</div>
        )}
        {/* Wishlist heart */}
        <div style={{
          position: 'absolute', top: 6, right: 6, width: 24, height: 24, borderRadius: '50%',
          background: 'rgba(255,255,255,.88)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 12, cursor: 'pointer', border: 'none', transition: 'transform .15s',
          color: isWatchlisted ? RED : INK4,
        }}
          onClick={e => { e.stopPropagation(); onToggleWatchlist(lot.id); }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.15)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
        >{isWatchlisted ? '♥' : '♡'}</div>
        {/* Countdown */}
        {countdown && !isEffectivelyEnded(lot) && (
          <div style={{
            position: 'absolute', bottom: 5, right: 5, fontFamily: 'var(--mono,monospace)', fontSize: 9,
            padding: '2px 5px', borderRadius: 3,
            background: urgent ? 'rgba(155,35,53,.85)' : 'rgba(26,20,16,.72)', color: '#fff',
          }}>{countdown}</div>
        )}
      </div>
      {/* Body */}
      <div style={{ padding: '9px 10px' }}>
        {/* 品牌 + 编号 同行 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 9, color: INK4, letterSpacing: .8, textTransform: 'uppercase' }}>
            {lot.brand || lot.category || ''}
          </div>
          {(lot.box_no || lot.lot_number) && (
            <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 9, color: INK3, letterSpacing: .5, flexShrink: 0 }}>
              {t('browse.lotNo')}:{lot.box_no && lot.item_no
                ? `${lot.box_no}-${String(lot.item_no).padStart(2, '0')}`
                : (lot.box_no || lot.lot_number)}
            </div>
          )}
        </div>
        <div style={{
          fontSize: 11, fontWeight: 500, color: INK, lineHeight: 1.4, marginBottom: 6,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          minHeight: 30,
        }}>{lot.title_zh || lot.title_ja || t('browse.noTitle')}</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginBottom: 5 }}>
          <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 13, fontWeight: 500, color: INK }}>
            {formatJPY(currentPrice)}
          </span>
          <span style={{ fontSize: 10, color: INK4 }}>≈¥{cnyCurrent.toLocaleString()}</span>
        </div>
        {/* 剩余时间 */}
        {countdownLong && !isEffectivelyEnded(lot) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 3, marginBottom: 5,
            fontFamily: 'var(--mono,monospace)', fontSize: 9,
            color: RED,
          }}>
            <span style={{ fontSize: 10 }}>⏱</span>
            {countdownLong}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 10, color: INK4 }}>🔨 {lot.bid_type === 'sealed' ? '非公開' : `${lot.bid_count || 0}${t('browse.bidTimes')}`}</span>
          {!isEffectivelyEnded(lot) && (
            <button style={{
              fontSize: 10, padding: '3px 7px', borderRadius: 3,
              background: 'rgba(184,137,42,.12)', color: GOLD, border: '1px solid rgba(184,137,42,.2)',
              cursor: 'pointer', fontWeight: 500, transition: 'all .12s',
            }}
              onClick={e => { e.stopPropagation(); onOpenDetail ? onOpenDetail(lot) : navigate(`/lots/${lot.id}`); }}
              onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(184,137,42,.12)'; e.currentTarget.style.color = GOLD; }}
            >{t('browse.bidNow')}</button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── FilterSection — 可折叠分区 ── */
function FilterSection({ title, expanded, onToggle, children, activeCount = 0 }) {
  return (
    <div style={{ borderBottom: `1px solid ${BD2}` }}>
      <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', cursor: 'pointer', userSelect: 'none', WebkitTapHighlightColor: 'transparent' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: INK }}>{title}</span>
          {activeCount > 0 && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 10, background: GOLD, color: '#fff', fontWeight: 700 }}>{activeCount}</span>}
        </div>
        <span style={{ fontSize: 13, color: INK4, display: 'inline-block', transform: expanded ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform .2s' }}>∧</span>
      </div>
      {expanded && <div style={{ padding: '0 16px 16px' }}>{children}</div>}
    </div>
  );
}

/* ── 以图搜索结果条 —— 识图中/命中数/AI 识别描述/一键清除 ── */
function AiImageResultStrip({ meta, searching, onClear }) {
  const { t } = useTranslation();
  const isError = meta?.type === 'error';
  return (
    <div style={{
      flexShrink: 0, padding: '6px 10px', borderBottom: `1px solid ${BD2}`, background: '#fff',
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      {searching && !meta ? (
        <span style={{ fontSize: 11, color: GOLD }}>{t('smartSearch.analyzing')}</span>
      ) : isError ? (
        <span style={{ fontSize: 11, color: RED, flex: 1 }}>{meta.message}</span>
      ) : (
        <>
          <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500, background: 'rgba(184,137,42,.1)', color: GOLD, whiteSpace: 'nowrap' }}>
            📷 {t('smartSearch.aiImage')} · {t('smartSearch.resultCount', { count: meta?.total ?? 0 })}
          </span>
          {meta?.analysis?.description && (
            <span style={{ fontSize: 11, color: INK3, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {meta.analysis.description}
            </span>
          )}
        </>
      )}
      <button type="button" onClick={onClear}
        style={{ marginLeft: 'auto', width: 24, height: 24, borderRadius: '50%', border: `1px solid ${BD}`, background: BG2, cursor: 'pointer', fontSize: 12, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}
      >✕</button>
    </div>
  );
}

/* ── 搜索历史(localStorage,与 Search 页共用同一 key,历史互通) ── */
const LS_HISTORY = 'yc_search_history';
const MAX_HISTORY = 12;
function loadHistory() {
  try { const v = JSON.parse(localStorage.getItem(LS_HISTORY) || '[]'); return Array.isArray(v) ? v.filter(x => typeof x === 'string') : []; }
  catch { return []; }
}
function pushHistory(term) {
  const t = String(term || '').trim();
  if (!t) return loadHistory();
  const list = loadHistory().filter(x => x !== t);
  list.unshift(t);
  const next = list.slice(0, MAX_HISTORY);
  try { localStorage.setItem(LS_HISTORY, JSON.stringify(next)); } catch {}
  return next;
}

/* ── 历史搜索 + 热门搜索面板(点搜索框弹出,像淘宝/qqdaipai) ── */
function HistoryHotPanel({ history, hot, onPick, onClearHistory }) {
  const { t } = useTranslation();
  if (!history.length && !hot.length) return null;
  const Chip = ({ text }) => (
    <button type="button" onClick={() => onPick(text)}
      style={{ padding: '5px 12px', borderRadius: 16, border: `1px solid ${BD2}`, background: BG2, color: INK2, fontSize: 12, cursor: 'pointer', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >{text}</button>
  );
  return (
    <div style={{ padding: '10px 12px 14px', background: '#fff', borderBottom: `1px solid ${BD2}` }}>
      {history.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: INK3 }}>{t('browse.searchHistory')}</span>
            <button type="button" onClick={onClearHistory} style={{ border: 'none', background: 'none', color: INK4, fontSize: 12, cursor: 'pointer', padding: 0 }}>{t('browse.clearHistory')}</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: hot.length ? 14 : 0 }}>
            {history.map((h, i) => <Chip key={`h${i}`} text={h} />)}
          </div>
        </>
      )}
      {hot.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 600, color: INK3, marginBottom: 8 }}>{t('browse.hotSearch')}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {hot.map((h, i) => <Chip key={`t${i}`} text={h} />)}
          </div>
        </>
      )}
    </div>
  );
}

/* ── 搜索/筛选无结果空态 —— 明确告诉用户"没找到",而不是掉回会场列表让人以为搜索坏了 ── */
function NoSearchResult({ keyword, onClear }) {
  const { t } = useTranslation();
  return (
    <div style={{ padding: '56px 24px', textAlign: 'center' }}>
      <svg width="80" height="80" viewBox="0 0 90 90" fill="none" style={{ marginBottom: 16, opacity: 0.5 }}>
        <circle cx="38" cy="38" r="22" stroke="#B8892A" strokeWidth="4" fill="none" />
        <line x1="54" y1="54" x2="74" y2="74" stroke="#B8892A" strokeWidth="5" strokeLinecap="round" />
        <circle cx="68" cy="22" r="10" fill="#EDE8E0" stroke="#C8B89A" strokeWidth="1.5" />
        <line x1="64" y1="18" x2="72" y2="26" stroke="#9B2335" strokeWidth="2" strokeLinecap="round" />
        <line x1="72" y1="18" x2="64" y2="26" stroke="#9B2335" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#5A4A3A', marginBottom: 8 }}>
        {keyword ? t('browse.noResultFor', { keyword }) : t('browse.noResult')}
      </div>
      <div style={{ fontSize: 12.5, color: INK4, lineHeight: 1.8, maxWidth: 300, margin: '0 auto 20px' }}>
        {t('browse.noResultHint')}
      </div>
      {onClear && (
        <button onClick={onClear}
          style={{ padding: '9px 22px', borderRadius: 8, border: `1px solid ${GOLD}`, background: 'rgba(184,137,42,.08)', color: GOLD, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
        >{t('browse.clearSearch')}</button>
      )}
    </div>
  );
}

/* ── PriceRangeInput — 价格区间输入 ── */
function PriceRangeInput({ minVal, maxVal, onChangeMin, onChangeMax }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input type="number" min="0" value={minVal} onChange={e => onChangeMin(e.target.value)} placeholder={t('browse.priceMin')}
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', border: `1px solid ${BD}`, borderRadius: 8, fontSize: 13, color: INK, outline: 'none', background: '#fff', WebkitAppearance: 'none' }}
      />
      <input type="number" min="0" value={maxVal} onChange={e => onChangeMax(e.target.value)} placeholder={t('browse.priceMax')}
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 10px', border: `1px solid ${BD}`, borderRadius: 8, fontSize: 13, color: INK, outline: 'none', background: '#fff', WebkitAppearance: 'none' }}
      />
    </div>
  );
}

/* ── ChipGroup — 多选 chip 组 ── */
function ChipGroup({ items, selected, onToggle, labelKey = 'label', valueKey = 'value', translate = false }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {items.map(item => {
        const val = typeof item === 'string' ? item : item[valueKey];
        const lbl = typeof item === 'string' ? item : item[labelKey];
        const active = selected.includes(val);
        return (
          <button key={val} onClick={() => onToggle(val)} style={{
            padding: '7px 15px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
            border: `1.5px solid ${active ? GOLD : BD}`,
            background: active ? 'rgba(184,137,42,.1)' : '#fff',
            color: active ? GOLD : INK2, fontWeight: active ? 600 : 400,
            transition: 'all .12s', WebkitTapHighlightColor: 'transparent',
          }}>{lbl}</button>
        );
      })}
    </div>
  );
}

/* ── FilterSheet — 底部筛选抽屉（移动端）── */
function FilterSheet({ open, onClose, onApply, sessions, brandList, initialFilter, appliedSession }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialFilter || EMPTY_FILTER);
  const [draftSession, setDraftSession] = useState(appliedSession || null);
  const [expanded, setExpanded] = useState({ price: true, start: true, buynow: false, category: true, session: true, brand: true, grade: true });

  // 每次打开同步初始值
  useEffect(() => {
    if (open) {
      setDraft(initialFilter || EMPTY_FILTER);
      setDraftSession(appliedSession || null);
    }
  }, [open]); // eslint-disable-line

  const toggleExpand = key => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // 品类↔品牌联动：草稿分类(或会场)变化时，品牌列表只显示该分类下的品牌
  const [sheetBrands, setSheetBrands] = useState(brandList);
  useEffect(() => {
    if (!open) return;
    const cats = draft.categories || [];
    const sessionId = draftSession?.id;
    // 未选分类且未选会场 → 用父级的全量品牌
    if (!cats.length && !sessionId) { setSheetBrands(brandList); return; }
    let cancelled = false;
    const reqs = (cats.length ? cats : [null]).map(c => {
      const params = {};
      if (c) params.category = c;
      if (sessionId) params.session_id = sessionId;
      return api.get('/lots/brands', { params }).then(r => r.data?.data || []).catch(() => []);
    });
    Promise.all(reqs).then(lists => {
      if (cancelled) return;
      const m = new Map();
      lists.flat().forEach(b => m.set(b.brand, (m.get(b.brand) || 0) + (b.count || b.cnt || 0)));
      setSheetBrands([...m.entries()].sort((a, b) => b[1] - a[1]).map(([brand, count]) => ({ brand, count })));
    });
    return () => { cancelled = true; };
  }, [draft.categories, draftSession, brandList, open]);

  const toggleArr = (key, val) => setDraft(prev => {
    const arr = prev[key] || [];
    return { ...prev, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
  });

  const countActive = () => {
    let n = 0;
    if (draft.priceMin || draft.priceMax) n++;
    if (draft.startMin || draft.startMax) n++;
    if (draft.buynowMin || draft.buynowMax) n++;
    if (draft.categories.length) n++;
    if (draft.grades.length) n++;
    if (draft.brands.length) n++;
    if (draftSession) n++;
    return n;
  };

  // 锁定 body 滚动
  useEffect(() => {
    if (open) { document.body.style.overflow = 'hidden'; }
    else { document.body.style.overflow = ''; }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(26,22,18,.45)', backdropFilter: 'blur(2px)' }} />
      {/* Sheet */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 601,
        background: '#FAF8F5', borderRadius: '16px 16px 0 0',
        maxHeight: '55vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -8px 40px rgba(26,22,18,.22)',
      }}>
        {/* Handle + Header */}
        <div style={{ flexShrink: 0, borderBottom: `1px solid ${BD2}`, background: '#fff', borderRadius: '16px 16px 0 0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 4 }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#C8C0B4' }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', padding: '8px 16px 12px', gap: 8 }}>
            <span style={{ flex: 1, fontSize: 16, fontWeight: 700, color: INK }}>{t('browse.filter')}</span>
            {countActive() > 0 && (
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(155,35,53,.1)', color: RED, fontWeight: 600 }}>
                {t('browse.selectedCount', { count: countActive() })}
              </span>
            )}
            <button onClick={onClose} style={{ width: 28, height: 28, border: 'none', borderRadius: '50%', background: BG3, cursor: 'pointer', fontSize: 14, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center', WebkitTapHighlightColor: 'transparent' }}>✕</button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#FAF8F5' }}>

          {/* 一级分类（置顶，选了分类下方品牌栏联动只显示该类品牌） */}
          <FilterSection title={t('browse.filterCategory')} expanded={expanded.category} onToggle={() => toggleExpand('category')}
            activeCount={draft.categories.length}>
            <ChipGroup items={FILTER_CATS} selected={draft.categories} onToggle={v => toggleArr('categories', v)} />
          </FilterSection>

          {/* 品牌（跟随上方所选分类联动） */}
          <FilterSection title={t('browse.filterBrand')} expanded={expanded.brand} onToggle={() => toggleExpand('brand')}
            activeCount={draft.brands.length}>
            {sheetBrands.length > 0 ? (
              <ChipGroup
                items={sheetBrands.map(b => b.brand)}
                selected={draft.brands}
                onToggle={v => toggleArr('brands', v)}
              />
            ) : (
              <div style={{ fontSize: 12, color: INK4, padding: '4px 0' }}>{t('browse.noBrandData')}</div>
            )}
          </FilterSection>

          {/* 当前价 */}
          <FilterSection title={t('browse.currentPrice')} expanded={expanded.price} onToggle={() => toggleExpand('price')}
            activeCount={(draft.priceMin || draft.priceMax) ? 1 : 0}>
            <PriceRangeInput minVal={draft.priceMin} maxVal={draft.priceMax}
              onChangeMin={v => setDraft(p => ({ ...p, priceMin: v }))}
              onChangeMax={v => setDraft(p => ({ ...p, priceMax: v }))} />
          </FilterSection>

          {/* 起拍价 */}
          <FilterSection title={t('browse.startPrice')} expanded={expanded.start} onToggle={() => toggleExpand('start')}
            activeCount={(draft.startMin || draft.startMax) ? 1 : 0}>
            <PriceRangeInput minVal={draft.startMin} maxVal={draft.startMax}
              onChangeMin={v => setDraft(p => ({ ...p, startMin: v }))}
              onChangeMax={v => setDraft(p => ({ ...p, startMax: v }))} />
          </FilterSection>

          {/* 一口价 */}
          <FilterSection title={t('browse.buyNowPrice')} expanded={expanded.buynow} onToggle={() => toggleExpand('buynow')}
            activeCount={(draft.buynowMin || draft.buynowMax) ? 1 : 0}>
            <PriceRangeInput minVal={draft.buynowMin} maxVal={draft.buynowMax}
              onChangeMin={v => setDraft(p => ({ ...p, buynowMin: v }))}
              onChangeMax={v => setDraft(p => ({ ...p, buynowMax: v }))} />
          </FilterSection>

          {/* 会场 */}
          <FilterSection title={t('browse.filterSession')} expanded={expanded.session} onToggle={() => toggleExpand('session')}
            activeCount={draftSession ? 1 : 0}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sessions.filter(s => Number(s.lot_count ?? s.active_lot_count ?? 0) > 0).length === 0 && <div style={{ color: INK4, fontSize: 13, padding: '4px 0' }}>{t('browse.noSessions')}</div>}
              {sessions.filter(s => Number(s.lot_count ?? s.active_lot_count ?? 0) > 0).map(sess => {
                const isSel = draftSession?.id === sess.id;
                return (
                  <div key={sess.id} onClick={() => setDraftSession(isSel ? null : sess)} style={{
                    padding: '11px 14px', borderRadius: 10, cursor: 'pointer',
                    border: `1.5px solid ${isSel ? BLUE : BD}`,
                    background: isSel ? 'rgba(43,92,230,.06)' : '#fff',
                    fontSize: 14, fontWeight: isSel ? 600 : 400,
                    color: isSel ? BLUE : INK, transition: 'all .12s',
                    WebkitTapHighlightColor: 'transparent',
                  }}>
                    {sess.house_name && <span style={{ color: isSel ? 'rgba(43,92,230,.7)' : INK4, marginRight: 5, fontSize: 12 }}>{sess.house_name}</span>}
                    {sess.name}
                  </div>
                );
              })}
            </div>
          </FilterSection>


          {/* 成色 */}
          <FilterSection title={t('browse.grade')} expanded={expanded.grade} onToggle={() => toggleExpand('grade')}
            activeCount={draft.grades.length}>
            <ChipGroup items={GRADES} selected={draft.grades} onToggle={v => toggleArr('grades', v)} />
          </FilterSection>

          <div style={{ height: 20 }} />
        </div>

        {/* Footer */}
        <div style={{ flexShrink: 0, borderTop: `1px solid ${BD2}`, padding: '12px 16px', display: 'flex', gap: 10, background: '#fff' }}>
          <button
            onClick={() => { setDraft(EMPTY_FILTER); setDraftSession(null); }}
            style={{ flex: 1, padding: '13px 0', border: `1px solid ${BD}`, borderRadius: 10, background: '#fff', color: INK3, fontSize: 15, fontWeight: 500, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
          >{t('common.reset')}</button>
          <button
            onClick={() => { onApply(draft, draftSession); onClose(); }}
            style={{ flex: 2, padding: '13px 0', border: 'none', borderRadius: 10, background: BLUE, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
          >
            {t('common.confirm')}{countActive() > 0 ? `（${countActive()}）` : ''}
          </button>
        </div>
      </div>
    </>
  );
}

/* -- Main Browse Page -- */
export default function Browse() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // 返回恢复:modal 场景列表本就不卸载;但进登录/委托/其他路由页再 back 回来会重挂载,
  // 页码/筛选/场次选择/滚动全部重置回起始点 —— 从快照(sessionStorage,仅 POP 恢复)找回。
  const [restored, saveListState] = useBackRestore();

  const [lots, setLots] = useState([]);
  const [jpyCnyRate, setJpyCnyRate] = useState(JPY_CNY_FALLBACK);
  const [dialogLot, setDialogLot] = useState(null); // { id, titleJa }

  // 详情是 modal(非路由)。modal 打开时 pushState 一条历史;浏览器/手机返回键触发 popstate →
  // 只关掉 modal、停在列表原位(页数/筛选/滚动都保留,因为列表从未卸载),而不是退回上级/首页。
  useEffect(() => {
    if (!dialogLot) return;
    window.history.pushState({ lotModal: true }, '');
    const onPop = () => setDialogLot(null);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [dialogLot]);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 动态加载实时汇率（CNY/JPY）
  useEffect(() => {
    api.get('/exchange-rates/current').then(({ data }) => {
      const rate = data?.rate || data?.conservative_rate;
      if (rate && rate > 0) setJpyCnyRate(Number(rate));
    }).catch(() => {}); // 失败时沿用 fallback
  }, []);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => restored?.page || 1);
  const [pageSize] = useState(50);
  const [category, setCategory] = useState(() => restored?.category || '');
  // 支持从搜索页跳转带入关键词：/browse?keyword=石川畅子
  const [searchParams] = useSearchParams();
  const _urlKeyword = searchParams.get('keyword') || searchParams.get('q') || '';
  // 返回恢复时以快照关键词为准(用户可能在页内改过);全新进入用 URL 带入词
  const _initKeyword = restored ? (restored.keyword || '') : _urlKeyword;
  const [keyword, setKeyword] = useState(_initKeyword);
  const [searchInput, setSearchInput] = useState(_initKeyword);
  const [searchFocused, setSearchFocused] = useState(false);      // 搜索框聚焦→显示历史/热门面板
  const [searchHistory, setSearchHistory] = useState(loadHistory);
  const [hotKeywords, setHotKeywords] = useState([]);             // 热门搜索(取热门品牌)
  const [aiResults, setAiResults] = useState(null); // AI search results override
  const [aiMeta, setAiMeta] = useState(null);
  const [aiSearching, setAiSearching] = useState(false);
  const [sort, setSort] = useState(() => restored?.sort || 'box_no');
  const [isListView, setIsListView] = useState(() => restored?.isListView || false);

  // Houses & sessions from API
  const [houses, setHouses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [activeHouse, setActiveHouse] = useState(() => restored?.activeHouse ?? null); // expanded house id
  const [selectedSession, setSelectedSession] = useState(() => restored?.selectedSession ?? null);

  // Watchlist
  const [watchlistedIds, setWatchlistedIds] = useState(new Set());
  const [watchlistLoaded, setWatchlistLoaded] = useState(false);

  // Bidding lots (from lots API response)
  const [biddingIds, setBiddingIds] = useState(new Set());

  // Mobile: house-session dropdown popup
  const [houseSheetOpen, setHouseSheetOpen] = useState(false);
  const [sheetHouseId, setSheetHouseId] = useState(null);
  const [sheetAnchor, setSheetAnchor] = useState({ x: 0, y: 0 }); // popup position

  // PC: inline session panel (replaces dropdown)
  const [pcExpandedHouseId, setPcExpandedHouseId] = useState(null);

  // Brand filter（旧 popover 方式，保持兼容）
  const [brandList, setBrandList] = useState([]);       // { brand, count }[]
  const [selectedBrands, setSelectedBrands] = useState(() => new Set(restored?.selectedBrands || []));
  const [brandPopoverOpen, setBrandPopoverOpen] = useState(false);
  const brandBtnRef = useRef(null);

  // 高级筛选 Sheet
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [appliedFilter, setAppliedFilter] = useState(() => restored?.appliedFilter || EMPTY_FILTER);

  // 状态一变就写快照;返回(POP)重挂载时用它恢复。滚动由 useScrollRestore 持续记录。
  useEffect(() => {
    saveListState({
      page, category, keyword, sort, isListView, activeHouse,
      selectedSession, selectedBrands: [...selectedBrands], appliedFilter,
    });
  }, [saveListState, page, category, keyword, sort, isListView, activeHouse,
    selectedSession, selectedBrands, appliedFilter]);

  // Fetch houses
  useEffect(() => {
    api.get('/auction-houses').then(({ data }) => {
      setHouses(data.data || data || []);
    }).catch(() => {});
  }, []);

  // Fetch sessions
  useEffect(() => {
    api.get('/sessions').then(({ data }) => {
      setSessions(data.data || data || []);
    }).catch(() => {});
  }, []);

  // Fetch watchlist
  useEffect(() => {
    api.get('/watchlist').then(({ data }) => {
      const ids = (data.data || data || []).map(w => w.id);
      setWatchlistedIds(new Set(ids));
      setWatchlistLoaded(true);
    }).catch(() => { setWatchlistLoaded(true); });
  }, []);

  const toggleWatchlist = async (lotId) => {
    if (watchlistedIds.has(lotId)) {
      try {
        await api.delete(`/watchlist/${lotId}`);
        setWatchlistedIds(prev => { const next = new Set(prev); next.delete(lotId); return next; });
        message.success(t('browse.unwatchlistSuccess'));
      } catch { message.error(t('browse.unwatchlistFailed')); }
    } else {
      try {
        await api.post('/watchlist', { lot_id: lotId });
        setWatchlistedIds(prev => new Set(prev).add(lotId));
        message.success(t('browse.watchlistSuccess'));
      } catch { message.error(t('browse.watchlistFailed')); }
    }
  };

  // Fetch brand list when category / session changes
  const _brandResetSkipFirst = useRef(true);
  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (selectedSession) params.session_id = selectedSession.id;
    api.get('/lots/brands', { params }).then(({ data }) => {
      const list = data.data || [];
      setBrandList(list);
      // 热门搜索：无场次/无品类时取全站热门品牌前 10 个,作为点一下即搜的热门词
      if (!category && !selectedSession) {
        setHotKeywords(list.slice(0, 10).map(b => b.brand || b.label).filter(Boolean));
      }
    }).catch(() => {});
    // reset on filter change —— 首挂载跳过,否则会把「返回恢复」的品牌选择清掉
    if (_brandResetSkipFirst.current) _brandResetSkipFirst.current = false;
    else setSelectedBrands(new Set());
  }, [category, selectedSession]);

  const fetchLots = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (category) params.category = category;
      if (keyword) params.keyword = keyword;
      if (sort) params.sort = sort;

      // 会场：优先使用 appliedFilter 中选择的 session，其次用顶部场馆 icon 点选的 selectedSession
      const activeSession = appliedFilter._session || selectedSession;
      if (activeSession) params.session_id = activeSession.id;

      // 品牌：appliedFilter 优先，其次旧 selectedBrands
      if (appliedFilter.brands && appliedFilter.brands.length > 0) {
        params.brands = appliedFilter.brands.join(',');
      } else if (selectedBrands.size > 0) {
        params.brands = [...selectedBrands].join(',');
      }

      // 高级筛选参数
      if (appliedFilter.categories && appliedFilter.categories.length > 0)
        params.categories = appliedFilter.categories.join(',');
      if (appliedFilter.priceMin)  params.price_min  = appliedFilter.priceMin;
      if (appliedFilter.priceMax)  params.price_max  = appliedFilter.priceMax;
      if (appliedFilter.startMin)  params.start_min  = appliedFilter.startMin;
      if (appliedFilter.startMax)  params.start_max  = appliedFilter.startMax;
      if (appliedFilter.buynowMin) params.buynow_min = appliedFilter.buynowMin;
      if (appliedFilter.buynowMax) params.buynow_max = appliedFilter.buynowMax;
      if (appliedFilter.grades && appliedFilter.grades.length > 0)
        params.grades = appliedFilter.grades.join(',');

      const { data } = await api.get('/lots', { params });
      const newLots = data.data || [];
      const newBiddingIds = new Set(newLots.filter(l => l.has_active_mandate).map(l => l.id));
      setBiddingIds(newBiddingIds);
      setLots(newLots);
      setTotal(data.total || 0);
    } catch { }
    finally { setLoading(false); }
  }, [page, pageSize, category, keyword, sort, selectedSession, selectedBrands, appliedFilter]);

  useEffect(() => { fetchLots(); }, [fetchLots]);

  // Auto-refresh: 每3分钟静默刷新列表价格，tab不可见时暂停
  useEffect(() => {
    const silentRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      fetchLots();
    };
    const timer = setInterval(silentRefresh, 180_000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchLots(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); };
  }, [fetchLots]);

const resetAndFetch = (setter, value) => { setter(value); setPage(1); };

  // 执行搜索：记历史 + 收起面板 + 触发查询(空词=清空)
  const doSearch = (term) => {
    const kw = String(term ?? searchInput).trim();
    setSearchInput(kw);
    if (kw) setSearchHistory(pushHistory(kw));
    setSearchFocused(false);
    resetAndFetch(setKeyword, kw);
  };

  // Group sessions by house
  const sessionsByHouse = {};
  sessions.forEach(s => {
    const hid = s.house_id || s.house_name;
    if (!sessionsByHouse[hid]) sessionsByHouse[hid] = [];
    sessionsByHouse[hid].push(s);
  });

  const _now = Date.now();
  // 14天内结束才算"正在进行"的合理范围（防止年份存错导致100+天倒计时）
  const _14days = 14 * 24 * 60 * 60 * 1000;
  const isSessionLive = s => {
    // end_at 超过14天后才结束的，说明数据库年份有误，不当作 live
    if (s.end_at && new Date(s.end_at).getTime() > _now + _14days) return false;
    // 入札開始(start_at)がまだ未来なら、DB status='active' でも「進行中」ではない。
    // これを見ないとカードが「正在进行」、詳細が「尚未开始（图录即将发布）」で矛盾する(GTA 等)。
    if (s.start_at && new Date(s.start_at).getTime() > _now) return false;
    if (s.status === 'live' || s.status === 'active' || s.status === 'settling') return true;
    // Fallback: 入札開始(preview_at)を過ぎていれば live 扱い（DB status が stale な場合の保険）
    return s.preview_at && new Date(s.preview_at).getTime() <= _now &&
      s.end_at && new Date(s.end_at).getTime() >= _now;
  };
  // 「尚未开始」判定: start_at（入札開始）が未来。下見期間も入札期間もまだ始まってない
  const isSessionNotStarted = s => {
    if (!s.start_at) return false;
    return new Date(s.start_at).getTime() > _now;
  };
  // 場次の入札開始日時を「M/D HH:MM」フォーマットで（カード表示用）
  const fmtStartShort = s => {
    if (!s.start_at) return '';
    const d = new Date(s.start_at);
    if (isNaN(d.getTime())) return '';
    const m = d.getMonth() + 1, day = d.getDate();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${m}/${day} ${hh}:${mm}`;
  };
  // 「有拍品」判定：场次里有拍品才展示（点进去空场次没意义）。lot_count 由 /sessions 返回。
  const sessionHasLots = s => Number(s.lot_count ?? s.active_lot_count ?? 0) > 0;
  // 顶部拍卖行 row：只保留「至少有一个带拍品场次」的拍行，屏蔽没有拍品的拍行。
  const housesWithLots = houses.filter(h => {
    const hs = sessionsByHouse[h.id] || sessionsByHouse[h.name] || [];
    return hs.some(sessionHasLots);
  });
  const liveSessions = sessions.filter(s => isSessionLive(s) && sessionHasLots(s));
  // 即将开始组：非进行中 + 有拍品，且(状态 preview/upcoming 或 入札開始未来)。
  // 后一条捕获「DB status=active 但 start_at 未来」的场次(如 GTA),否则修正 isSessionLive 后它会从列表消失。
  const previewSessions = sessions.filter(s => !isSessionLive(s) && sessionHasLots(s) && (s.status === 'preview' || s.status === 'upcoming' || isSessionNotStarted(s)));

  /** 场次是否已结束：end_at 已过 或 全部拍品已关闭 */
  const allClosed = s => {
    // end_at 已过期（且不是年份错误的未来日期）
    if (s.end_at) {
      const endTs = new Date(s.end_at).getTime();
      if (endTs < _now && endTs > 0) return true;
    }
    return Number(s.lot_count) > 0 && Number(s.closed_count) >= Number(s.lot_count);
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  const mainRef = useRef(null);
  const scrollToTop = () => {
    if (mainRef.current) mainRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // 返回恢复滚动位置(Browse 滚动在内部容器 mainRef,不在 window)
  useScrollRestore(restored, saveListState, !loading && lots.length > 0, () => mainRef.current);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
    scrollToTop();
  };

  // Build page numbers: 1 2 3 4 5 ... N-1 N  (with ellipsis)
  const buildPageNums = () => {
    if (totalPages <= 9) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    const delta = 2;
    const left = page - delta;
    const right = page + delta;
    let last = 0;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= left && i <= right)) {
        if (last && i - last > 1) pages.push('…');
        pages.push(i);
        last = i;
      }
    }
    return pages;
  };

  /* ── 是否处于"搜索/筛选"态：有关键词 / 选了品类 tab / 用了筛选面板 / 以图搜物。
        用于区分"搜了但0结果"(应显示"没找到")与"什么都没搜"(才显示会场一览)。
        选拍卖行(activeHouse)属于浏览场次,不算搜索。 ── */
  const filterSheetActive = !!(
    appliedFilter.priceMin || appliedFilter.priceMax ||
    appliedFilter.startMin || appliedFilter.startMax ||
    appliedFilter.buynowMin || appliedFilter.buynowMax ||
    appliedFilter.categories?.length || appliedFilter.grades?.length || appliedFilter.brands?.length
  );
  const hasActiveQuery = !!keyword || !!category || !!aiResults || filterSheetActive;

  /* ─────────────────────────────── shared sort helper ─────────────────────────────── */
  // When AI search results are active, use them directly (already sorted by AI relevance)
  // 按 id 去重 —— 双保险:即便后端因数据重复/翻页错位返回了同一拍品,前端也不再重复渲染。
  const displayLots = (() => {
    const src = aiResults || lots;
    const seen = new Set();
    const out = [];
    for (const l of src) { if (l && !seen.has(l.id)) { seen.add(l.id); out.push(l); } }
    return out;
  })();
  const sortedLots = [...displayLots].sort((a, b) => {
    if (aiResults) return 0; // preserve AI ordering
    // 1. 结拍的放最后
    const endA = isEffectivelyEnded(a) ? 1 : 0, endB = isEffectivelyEnded(b) ? 1 : 0;
    if (endA !== endB) return endA - endB;
    // 2. 登录用户参与竞拍的放最前
    const bidA = (biddingIds.has(a.id) || !!a.has_active_mandate) ? 0 : 1;
    const bidB = (biddingIds.has(b.id) || !!b.has_active_mandate) ? 0 : 1;
    if (bidA !== bidB) return bidA - bidB;
    // 3. 竞拍中（session live / active / settling）的排在前面
    const isLotLive = lot => ['live', 'active', 'settling'].includes(lot.session_status);
    const liveA = isLotLive(a) ? 0 : 1, liveB = isLotLive(b) ? 0 : 1;
    if (liveA !== liveB) return liveA - liveB;
    // 4. 收藏的
    const isWat = id => watchlistLoaded ? watchlistedIds.has(id) : watchlistedIds.has(id);
    const watA = (isWat(a.id) || (!watchlistLoaded && !!a.is_watchlisted)) ? 0 : 1;
    const watB = (isWat(b.id) || (!watchlistLoaded && !!b.is_watchlisted)) ? 0 : 1;
    if (watA !== watB) return watA - watB;
    return 0;
  });

  return (
    <div style={{ fontFamily: 'var(--sans,sans-serif)', background: BG, color: INK, minHeight: 'calc(100vh - 120px)' }}>

    {/* ══════════════════════════ MOBILE LAYOUT ══════════════════════════ */}
    {isMobile ? (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>

        {/* ── House icons strip ── */}
        <div style={{
          flexShrink: 0, background: '#fff', borderBottom: `1px solid ${BD2}`,
          display: 'flex', alignItems: 'flex-end', padding: '8px 4px 8px',
          overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
          gap: 0,
        }}>
          {/* 全部 */}
          <div
            onClick={() => { setSelectedSession(null); setActiveHouse(null); setPage(1); }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '2px 8px', flexShrink: 0, cursor: 'pointer' }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: !selectedSession ? GOLD : BG3,
              border: `2px solid ${!selectedSession ? GOLD : 'transparent'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              transition: 'all .15s',
            }}>🏠</div>
            <span style={{ fontSize: 10, color: !selectedSession ? GOLD : INK3, fontWeight: !selectedSession ? 600 : 400, whiteSpace: 'nowrap' }}>{t('browse.allCategories')}</span>
          </div>

          {housesWithLots.map(house => {
            const houseId = house.id || house.name;
            const houseSessions = sessionsByHouse[houseId] || sessionsByHouse[house.name] || [];
            const hasLive = houseSessions.some(isSessionLive);
            const isActive = selectedSession && (
              selectedSession.house_id === houseId ||
              selectedSession.house_id === house.id ||
              selectedSession.house_name === house.name
            );
            return (
              <div
                key={houseId}
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setSheetAnchor({ x: rect.left + rect.width / 2, y: rect.bottom + 10 });
                  setSheetHouseId(houseId);
                  setHouseSheetOpen(true);
                }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '2px 8px', flexShrink: 0, cursor: 'pointer', position: 'relative' }}
              >
                {hasLive && (
                  <div style={{
                    position: 'absolute', top: 2, right: 10, width: 9, height: 9,
                    borderRadius: '50%', background: RED, border: '2px solid #fff', zIndex: 1,
                  }} />
                )}
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: house.logo_url ? (isActive ? 'rgba(184,137,42,.08)' : '#fff') : (isActive ? (house.color || '#374151') : (house.color ? house.color + '22' : BG3)),
                  border: `2px solid ${isActive ? (house.color || GOLD) : (house.color ? house.color + '55' : BD2)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, transition: 'all .15s',
                  overflow: 'hidden',
                }}>
                  {house.logo_url
                    ? <img src={house.logo_url} alt="" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
                    : (house.emoji || (house.name_zh || house.name || '?').charAt(0))}
                </div>
                <span style={{
                  fontSize: 10, color: isActive ? GOLD : INK3, fontWeight: isActive ? 600 : 400,
                  maxWidth: 56, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{house.name_zh || house.name}</span>
              </div>
            );
          })}
        </div>

        {/* ── Selected session banner ── */}
        {selectedSession && (() => {
          const sessEnded = allClosed(selectedSession);
          const st = sessEnded ? STATUS_MAP.closed : (STATUS_MAP[isSessionLive(selectedSession) ? 'live' : selectedSession.status] || STATUS_MAP.upcoming);
          return (
            <div style={{
              flexShrink: 0, background: 'rgba(184,137,42,.07)', borderBottom: `1px solid rgba(184,137,42,.2)`,
              padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: INK4, flexShrink: 0 }}>{selectedSession.house_name}</span>
              <span style={{ color: INK5, fontSize: 11 }}>›</span>
              <span style={{ fontSize: 12, fontWeight: 600, color: INK, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedSession.name}</span>
              <span style={{ fontSize: 10, color: INK4, fontFamily: 'var(--mono)', flexShrink: 0 }}>{t('browse.itemsCount', { count: selectedSession.lot_count || 0 })}</span>
              <button
                onClick={() => { setSelectedSession(null); setActiveHouse(null); setPage(1); }}
                style={{ width: 20, height: 20, border: 'none', background: 'rgba(0,0,0,.06)', borderRadius: '50%', cursor: 'pointer', fontSize: 12, color: INK3, padding: 0, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >✕</button>
            </div>
          );
        })()}

        {/* ── Search bar ── 2026-07-04: 手机键盘不出"搜索"键、无可见按钮 → form 包裹
            (提交即搜索)+enterKeyHint+可见搜索按钮 */}
        <form
          onSubmit={e => { e.preventDefault(); doSearch(searchInput); }}
          style={{
            flexShrink: 0, padding: '6px 10px', borderBottom: `1px solid ${BD3}`,
            display: 'flex', alignItems: 'center', gap: 6, background: '#fff',
          }}
        >
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onFocus={() => { setSearchHistory(loadHistory()); setSearchFocused(true); }}
              onKeyDown={e => { if (e.key === 'Enter') { doSearch(searchInput); } }}
              enterKeyHint="search"
              placeholder={t('browse.searchPlaceholder')}
              style={{
                width: '100%', padding: '7px 38px 7px 30px', border: `1px solid ${BD}`,
                borderRadius: 20, fontSize: 12, color: INK, background: BG, outline: 'none',
                WebkitAppearance: 'none',
              }}
            />
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke={INK4} strokeWidth="1.8" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="9" cy="9" r="5.5" /><line x1="13.5" y1="13.5" x2="17" y2="17" />
            </svg>
            {/* 以图搜物：淘宝式,相机图标放搜索框右侧 */}
            <ImageSearchButton
              onResults={(results, meta) => { setAiResults(results); setAiMeta(meta); }}
              onLoading={setAiSearching}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 24, height: 24, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          </div>
          {keyword && (
            <button type="button" onClick={() => { setSearchInput(''); resetAndFetch(setKeyword, ''); }}
              style={{ width: 26, height: 26, border: `1px solid ${BD}`, borderRadius: '50%', background: BG2, cursor: 'pointer', fontSize: 12, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}
            >✕</button>
          )}
          <button type="submit" style={{
            flexShrink: 0, padding: '7px 14px', border: 'none', borderRadius: 20,
            background: '#2f54eb', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1.2,
          }}>{t('browse.searchBtn', '搜索')}</button>
        </form>

        {/* ── 历史搜索 + 热门搜索(聚焦搜索框且未在搜索时弹出) ── */}
        {searchFocused && !keyword && !aiResults && (
          <HistoryHotPanel
            history={searchHistory} hot={hotKeywords}
            onPick={doSearch}
            onClearHistory={() => { try { localStorage.removeItem(LS_HISTORY); } catch {} setSearchHistory([]); }}
          />
        )}

        {/* ── 以图搜索结果条(识图中/结果数/清除) ── */}
        {(aiSearching || aiMeta) && (
          <AiImageResultStrip meta={aiMeta} searching={aiSearching}
            onClear={() => { setAiResults(null); setAiMeta(null); }} />
        )}

        {/* ── AI Recommendations ── */}
        {!keyword && !aiResults && !category && (
          <RecommendSection
            jpyCnyRate={jpyCnyRate}
            onLotClick={(lot) => setDialogLot({ id: lot.id, titleJa: lot.title_ja || lot.title || '' })}
          />
        )}

        {/* ── Toolbar ── */}
        <div style={{
          flexShrink: 0, padding: '6px 10px', borderBottom: `1px solid ${BD2}`,
          display: 'flex', alignItems: 'center', gap: 5, background: '#fff',
          overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
        }}>
          {CATEGORIES.map(cat => (
            <button key={cat.value} onClick={() => { if (cat.value !== category) resetAndFetch(setCategory, cat.value); }}
              style={{
                padding: '3px 9px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                color: category === cat.value ? '#fff' : INK3,
                background: category === cat.value ? INK : 'transparent',
                border: '1px solid transparent', transition: 'all .12s', whiteSpace: 'nowrap', flexShrink: 0,
              }}>
              {t(cat.labelKey)}
            </button>
          ))}
          <div style={{ width: 1, height: 14, background: BD2, flexShrink: 0 }} />
          {/* 筛选按钮 */}
          {(() => {
            const filterCount = [
              appliedFilter.priceMin || appliedFilter.priceMax,
              appliedFilter.startMin || appliedFilter.startMax,
              appliedFilter.buynowMin || appliedFilter.buynowMax,
              appliedFilter.categories?.length > 0,
              appliedFilter.grades?.length > 0,
              appliedFilter.brands?.length > 0,
              appliedFilter._session,
            ].filter(Boolean).length;
            const hasFilter = filterCount > 0;
            return (
              <button
                onClick={() => setFilterSheetOpen(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 3, padding: '3px 9px',
                  borderRadius: 20, fontSize: 11, cursor: 'pointer', flexShrink: 0,
                  color: hasFilter ? GOLD : INK3,
                  background: hasFilter ? 'rgba(184,137,42,.1)' : 'transparent',
                  border: `1px solid ${hasFilter ? GOLD : 'transparent'}`,
                  transition: 'all .12s', position: 'relative',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <line x1="1" y1="3" x2="11" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="2.5" y1="6" x2="9.5" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="4" y1="9" x2="8" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {t('browse.filter')}{hasFilter ? `(${filterCount})` : ''}
              </button>
            );
          })()}
          <div style={{ width: 1, height: 14, background: BD2, flexShrink: 0 }} />
          <select value={sort} onChange={e => resetAndFetch(setSort, e.target.value)}
            style={{ padding: '3px 5px', border: `1px solid ${BD}`, borderRadius: 4, fontSize: 11, color: INK3, background: '#fff', outline: 'none', cursor: 'pointer', flexShrink: 0 }}
          >
            <option value="box_no">{t('browse.sortBoxNo')}</option>
            <option value="end_asc">{t('browse.sortEndingSoon')}</option>
            <option value="bid_desc">{t('browse.sortPopular')}</option>
            <option value="price_desc">{t('browse.sortPriceDesc')}</option>
            <option value="price_asc">{t('browse.sortPriceAsc')}</option>
            <option value="newest">{t('browse.sortNewest')}</option>
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 10, color: INK4, fontFamily: 'var(--mono)', flexShrink: 0 }}>{t('browse.itemsCount', { count: total.toLocaleString() })}</span>
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={{
                width: 20, height: 20, border: `1px solid ${BD}`, borderRadius: 3, background: '#fff',
                color: page === 1 ? INK5 : INK3, fontSize: 11, cursor: page === 1 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}>‹</button>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: INK3, minWidth: 32, textAlign: 'center' }}>{page}/{totalPages}</span>
              <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={{
                width: 20, height: 20, border: `1px solid ${BD}`, borderRadius: 3, background: '#fff',
                color: page === totalPages ? INK5 : INK3, fontSize: 11, cursor: page === totalPages ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}>›</button>
            </div>
          )}
        </div>

        {/* ── Lot grid ── */}
        <div ref={mainRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
          <div style={{ padding: '10px 8px 48px' }}>
            <Spin spinning={loading && page === 1}>
              {lots.length === 0 && !loading ? (
                selectedSession ? (
                  isSessionNotStarted(selectedSession) ? (
                    /* 場次未开始 → 友好提示，不再显示「无优选拍品」误导信息 */
                    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#5A4A3A', marginBottom: 8, letterSpacing: 1 }}>
                        {t('browse.sessionNotStarted')}
                      </div>
                      <div style={{ fontSize: 13, color: INK3, lineHeight: 1.8 }}>
                        {t('browse.sessionScheduledAt')}
                        <span style={{ fontWeight: 600, color: GOLD, margin: '0 4px' }}>
                          {fmtStartShort(selectedSession) || '—'}
                        </span>
                        {t('browse.sessionStart')}
                      </div>
                      <div style={{ fontSize: 12, color: INK4, marginTop: 6 }}>
                        {t('browse.catalogWillPublish')}
                      </div>
                    </div>
                  ) : (
                  /* 有选场次但 0 件 → 该场次无优选拍品（类型不符） */
                  <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <svg width="90" height="90" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16, opacity: 0.55 }}>
                      {/* 放大镜 */}
                      <circle cx="38" cy="38" r="22" stroke="#B8892A" strokeWidth="4" fill="none" />
                      <line x1="54" y1="54" x2="74" y2="74" stroke="#B8892A" strokeWidth="5" strokeLinecap="round" />
                      {/* 镜内：钻石轮廓 */}
                      <polygon points="38,24 48,34 38,50 28,34" stroke="#B8892A" strokeWidth="2" fill="rgba(184,137,42,0.08)" strokeLinejoin="round" />
                      <line x1="28" y1="34" x2="48" y2="34" stroke="#B8892A" strokeWidth="1.5" opacity="0.5" />
                      {/* 右上角小 × */}
                      <circle cx="68" cy="22" r="10" fill="#EDE8E0" stroke="#C8B89A" strokeWidth="1.5" />
                      <line x1="64" y1="18" x2="72" y2="26" stroke="#9B2335" strokeWidth="2" strokeLinecap="round" />
                      <line x1="72" y1="18" x2="64" y2="26" stroke="#9B2335" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#5A4A3A', marginBottom: 6, letterSpacing: 1 }}>{t('browse.noFeaturedLots')}</div>
                    {(() => {
                      const house = houses.find(h => h.id === selectedSession?.house_id);
                      const cats = (house?.supported_categories || '')
                        .split(',').map(s => s.trim()).filter(Boolean);
                      const labels = cats.map(c => {
                        const found = CATEGORIES.find(x => x.value === c);
                        return found ? t(found.labelKey).replace(/^.\s/, '') : c;
                      });
                      return labels.length > 0 ? (
                        <div style={{ fontSize: 12, color: INK4, lineHeight: 1.7 }}>
                          {t('browse.noLotsForCategories', { categories: labels.join('、') })}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: INK4, lineHeight: 1.7 }}>{t('browse.noMatchingLots')}</div>
                      );
                    })()}
                  </div>
                  )
                ) : hasActiveQuery ? (
                  /* 搜了/筛了但0结果 → 明确"没找到"，不再掉回会场列表误导 */
                  <NoSearchResult keyword={keyword}
                    onClear={() => { setSearchInput(''); setCategory(''); setAiResults(null); setAiMeta(null); setAppliedFilter(EMPTY_FILTER); resetAndFetch(setKeyword, ''); }} />
                ) : (
                  /* 未选场次且未搜索 → 显示场次卡片列表 */
                  <div style={{ padding: '12px 10px' }}>
                    {[...liveSessions, ...previewSessions].length > 0 ? (
                      <>
                        <div style={{ fontSize: 11, fontWeight: 600, color: INK4, letterSpacing: 1, marginBottom: 8, paddingLeft: 4 }}>
                          会场一览 · {liveSessions.length + previewSessions.length} 个场次
                        </div>
                        {[...liveSessions, ...previewSessions].map(s => (
                          <SessionCard
                            key={s.id}
                            session={s}
                            onSelect={(sess) => { setActiveHouse(sess.house_id || sess.house_name); setSelectedSession(sess); setPage(1); }}
                            allClosedFn={allClosed}
                            isSessionLiveFn={isSessionLive}
                          />
                        ))}
                      </>
                    ) : (
                      <div style={{ padding: '60px 20px', textAlign: 'center', color: INK4 }}>
                        <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
                        <div style={{ fontSize: 14, color: INK3, marginBottom: 6 }}>{t('browse.noResults')}</div>
                        <div style={{ fontSize: 12, color: INK4 }}>{t('browse.selectAuctionHouse')}</div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 8 }}>
                    {sortedLots.map(lot => {
                      const isWatchlisted = watchlistLoaded ? watchlistedIds.has(lot.id) : (watchlistedIds.has(lot.id) || !!lot.is_watchlisted);
                      return <LotCard key={lot.id} lot={lot} isWatchlisted={isWatchlisted} isBidding={biddingIds.has(lot.id) || !!lot.has_active_mandate} onToggleWatchlist={toggleWatchlist} onOpenDetail={lot => setDialogLot({ id: lot.id, titleJa: lot.title_ja || lot.title || '' })} jpyCnyRate={jpyCnyRate} />;
                    })}
                  </div>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '20px 0 8px', userSelect: 'none' }}>
                      <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={{
                        width: 28, height: 28, border: `1px solid ${page === 1 ? BD2 : BD}`, borderRadius: 4,
                        background: '#fff', color: page === 1 ? INK5 : INK3, fontSize: 13,
                        cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>‹</button>
                      {buildPageNums().map((p, i) =>
                        p === '…' ? (
                          <span key={`e${i}`} style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: INK4, fontSize: 11 }}>…</span>
                        ) : (
                          <button key={p} onClick={() => goToPage(p)} style={{
                            width: 28, height: 28, border: `1px solid ${p === page ? GOLD : BD}`, borderRadius: 4,
                            background: p === page ? GOLD : '#fff', color: p === page ? '#fff' : INK3,
                            fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: p === page ? 600 : 400,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>{p}</button>
                        )
                      )}
                      <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={{
                        width: 28, height: 28, border: `1px solid ${page === totalPages ? BD2 : BD}`, borderRadius: 4,
                        background: '#fff', color: page === totalPages ? INK5 : INK3, fontSize: 13,
                        cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>›</button>
                    </div>
                  )}
                </>
              )}
            </Spin>
          </div>
        </div>

        {/* ── House Session Dropdown Popup ── */}
        {houseSheetOpen && (() => {
          const sheetHouse = houses.find(h => (h.id || h.name) === sheetHouseId);
          if (!sheetHouse) return null;
          const sheetSessions = (sessionsByHouse[sheetHouse.id || sheetHouse.name] || sessionsByHouse[sheetHouse.name] || []).filter(sessionHasLots);
          const POP_W = 280;
          const ARROW_H = 10;  // visible arrow height (px)
          const margin = 8;
          const screenW = window.innerWidth;
          // Center popup on icon, clamp to screen edges
          const rawLeft = sheetAnchor.x - POP_W / 2;
          const left = Math.max(margin, Math.min(rawLeft, screenW - POP_W - margin));
          // Arrow center relative to popup left edge (clamp so arrow stays inside card)
          const arrowCx = Math.max(14, Math.min(sheetAnchor.x - left, POP_W - 14));
          return (
            <>
              {/* Invisible backdrop */}
              <div
                onClick={() => setHouseSheetOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 299 }}
              />
              {/* Outer wrapper — positioned but NOT overflow:hidden so arrow is visible */}
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: sheetAnchor.y,
                  left,
                  width: POP_W,
                  maxHeight: '60vh',
                  zIndex: 300,
                  display: 'flex', flexDirection: 'column',
                }}
              >
                {/* ── Triangle arrow pointing UP ── */}
                {/* Outer triangle (border colour) */}
                <div style={{
                  position: 'absolute',
                  top: -(ARROW_H + 1),
                  left: arrowCx - (ARROW_H + 1),
                  width: 0, height: 0,
                  borderLeft: `${ARROW_H + 1}px solid transparent`,
                  borderRight: `${ARROW_H + 1}px solid transparent`,
                  borderBottom: `${ARROW_H + 1}px solid ${BD}`,
                  zIndex: 1,
                }} />
                {/* Inner triangle (white fill) */}
                <div style={{
                  position: 'absolute',
                  top: -ARROW_H,
                  left: arrowCx - ARROW_H,
                  width: 0, height: 0,
                  borderLeft: `${ARROW_H}px solid transparent`,
                  borderRight: `${ARROW_H}px solid transparent`,
                  borderBottom: `${ARROW_H}px solid #fff`,
                  zIndex: 2,
                }} />

                {/* Card (overflow:hidden only here, so arrow isn't clipped) */}
                <div style={{
                  background: '#fff', border: `1px solid ${BD}`, borderRadius: 10,
                  boxShadow: '0 8px 32px rgba(26,20,16,.18)',
                  display: 'flex', flexDirection: 'column',
                  overflow: 'hidden', maxHeight: '60vh',
                }}>
                {/* Header */}
                <div style={{ padding: '11px 14px 10px', borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                    background: sheetHouse.logo_url ? '#fff' : (sheetHouse.color || '#374151'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
                    overflow: 'hidden',
                  }}>
                    {sheetHouse.logo_url
                      ? <img src={sheetHouse.logo_url} alt="" style={{ width: '88%', height: '88%', objectFit: 'contain' }} />
                      : (sheetHouse.emoji || '🏢')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {sheetHouse.name_zh || sheetHouse.name}
                    </div>
                    {sheetHouse.name_ja && <div style={{ fontSize: 10, color: INK4 }}>{sheetHouse.name_ja}</div>}
                  </div>
                  <button onClick={() => setHouseSheetOpen(false)} style={{
                    width: 24, height: 24, border: `1px solid ${BD}`, borderRadius: 5,
                    background: BG2, cursor: 'pointer', fontSize: 13, color: INK3,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0,
                  }}>✕</button>
                </div>
                {/* Sessions */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '3px 0 4px' }}>
                  {sheetSessions.length === 0 && (
                    <div style={{ padding: '20px 14px', textAlign: 'center', color: INK4, fontSize: 12 }}>{t('browse.noSessions')}</div>
                  )}
                  {sheetSessions.map(sess => {
                    const sessEnded = allClosed(sess);
                    const effectiveStatus = isSessionLive(sess) ? 'live' : sess.status;
                    const st = sessEnded ? STATUS_MAP.closed : (STATUS_MAP[effectiveStatus] || STATUS_MAP.upcoming);
                    const isSel = selectedSession?.id === sess.id;
                    return (
                      <div
                        key={sess.id}
                        onClick={() => {
                          setSelectedSession(sess);
                          setActiveHouse(sess.house_id || sheetHouse.id || sheetHouse.name);
                          setPage(1);
                          setHouseSheetOpen(false);
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 9,
                          padding: '9px 14px', cursor: 'pointer',
                          background: isSel ? 'rgba(184,137,42,.06)' : 'transparent',
                          borderLeft: isSel ? `3px solid ${GOLD}` : '3px solid transparent',
                          borderBottom: `1px solid ${BD3}`, transition: 'background .1s',
                        }}
                      >
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: isSel ? 600 : 500, color: sessEnded ? INK4 : INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {sessEnded && <span style={{ marginRight: 2, opacity: .7, fontSize: 11 }}>{t('browse.sessionEnded')}</span>}{sess.name}
                          </div>
                          <div style={{ fontSize: 10, color: INK4, marginTop: 1 }}>
                            <span style={{ padding: '1px 4px', borderRadius: 3, background: st.bg, color: st.color, fontSize: 9, fontWeight: 600 }}>{t(st.labelKey)}</span>
                            {sess.end_at && <span style={{ marginLeft: 3 }}>{t('browse.deadline')} {fmtCST(sess.end_at, 'MM/DD HH:mm')}</span>}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 500, color: INK }}>{sess.lot_count || 0}</div>
                          <div style={{ fontSize: 9, color: INK4 }}>{t('browse.pieces')}</div>
                        </div>
                        {isSel && <span style={{ fontSize: 14, color: GOLD, flexShrink: 0 }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
                </div> {/* end card */}
              </div>   {/* end outer wrapper */}
            </>
          );
        })()}

        {/* ── FilterSheet — 筛选抽屉 ── */}
        <FilterSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          sessions={selectedSession ? (sessionsByHouse[selectedSession.house_id] || sessionsByHouse[selectedSession.house_name] || []) : activeHouse ? (sessionsByHouse[activeHouse] || []) : sessions}
          brandList={brandList}
          initialFilter={appliedFilter}
          appliedSession={appliedFilter._session || null}
          onApply={(newFilter, newSession) => {
            setAppliedFilter({ ...newFilter, _session: newSession || null });
            if (newSession) {
              setSelectedSession(newSession);
              setActiveHouse(newSession.house_id || null);
            }
            setPage(1);
          }}
        />
      </div>

    ) : (

    /* ══════════════════════════ DESKTOP LAYOUT ══════════════════════════ */
      <div style={{
        display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', overflow: 'hidden',
        maxWidth: 1200, margin: '0 auto', borderLeft: `1px solid ${BD2}`, borderRight: `1px solid ${BD2}`,
        position: 'relative',
      }}>

        {/* ── Top: House icons strip ── */}
        <div style={{
          flexShrink: 0, background: '#fff', borderBottom: `1px solid ${BD2}`,
          display: 'flex', alignItems: 'center', padding: '8px 16px',
          overflowX: 'auto', scrollbarWidth: 'none', gap: 0,
        }}>
          {/* 全部 */}
          <div
            onClick={() => { setSelectedSession(null); setActiveHouse(null); setPage(1); }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '2px 10px', flexShrink: 0, cursor: 'pointer' }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: '50%',
              background: !selectedSession && !activeHouse ? GOLD : BG3,
              border: `2px solid ${!selectedSession && !activeHouse ? GOLD : 'transparent'}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
              transition: 'all .15s',
            }}>🏠</div>
            <span style={{ fontSize: 10, color: !selectedSession && !activeHouse ? GOLD : INK3, fontWeight: !selectedSession && !activeHouse ? 600 : 400, whiteSpace: 'nowrap' }}>{t('browse.allCategories')}</span>
          </div>

          {housesWithLots.map(house => {
            const houseId = house.id || house.name;
            const houseSessions = sessionsByHouse[houseId] || sessionsByHouse[house.name] || [];
            const hasLive = houseSessions.some(isSessionLive);
            const isActive = selectedSession
              ? (selectedSession.house_id === houseId || selectedSession.house_id === house.id || selectedSession.house_name === house.name)
              : activeHouse === houseId;
            const isPcExpanded = pcExpandedHouseId === houseId;
            return (
              <div
                key={houseId}
                onClick={() => {
                  setPcExpandedHouseId(prev => prev === houseId ? null : houseId);
                }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '2px 10px', flexShrink: 0, cursor: 'pointer', position: 'relative' }}
              >
                {hasLive && (
                  <div style={{
                    position: 'absolute', top: 2, right: 12, width: 9, height: 9,
                    borderRadius: '50%', background: RED, border: '2px solid #fff', zIndex: 1,
                  }} />
                )}
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: house.logo_url ? (isActive || isPcExpanded ? 'rgba(184,137,42,.08)' : '#fff') : (isActive || isPcExpanded ? (house.color || '#374151') : (house.color ? house.color + '22' : BG3)),
                  border: `2px solid ${isActive || isPcExpanded ? (house.color || GOLD) : (house.color ? house.color + '55' : BD2)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, transition: 'all .15s',
                  overflow: 'hidden',
                }}>
                  {house.logo_url
                    ? <img src={house.logo_url} alt="" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
                    : (house.emoji || (house.name_zh || house.name || '?').charAt(0))}
                </div>
                <span style={{
                  fontSize: 10, color: isActive || isPcExpanded ? GOLD : INK3, fontWeight: isActive || isPcExpanded ? 600 : 400,
                  maxWidth: 64, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>{house.name_zh || house.name}</span>
              </div>
            );
          })}
        </div>

        {/* ── PC inline session panel ── */}
        {pcExpandedHouseId && (() => {
          const exHouse = houses.find(h => (h.id || h.name) === pcExpandedHouseId);
          if (!exHouse) return null;
          const exSessions = sessionsByHouse[pcExpandedHouseId] || sessionsByHouse[exHouse.name] || [];
          const exLive = exSessions.filter(s => isSessionLive(s) || s.status === 'preview');
          const exUpcoming = exSessions.filter(s => s.status === 'upcoming' && !isSessionLive(s));
          const exActive = [...exLive, ...exUpcoming];
          return (
            <div style={{
              flexShrink: 0, background: 'rgba(184,137,42,.03)',
              borderBottom: `1.5px solid rgba(184,137,42,.2)`,
              padding: '14px 20px',
            }}>
              {/* Panel header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                {exHouse.logo_url && (
                  <img src={exHouse.logo_url} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: 'contain', border: `1px solid ${BD2}` }} />
                )}
                <span style={{ fontSize: 14, fontWeight: 700, color: INK }}>{exHouse.name_zh || exHouse.name}</span>
                <span style={{ fontSize: 12, color: INK4 }}>— 场次列表</span>
                <button
                  onClick={() => setPcExpandedHouseId(null)}
                  style={{ marginLeft: 'auto', width: 22, height: 22, border: 'none', background: BG3, borderRadius: '50%', cursor: 'pointer', fontSize: 12, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                >✕</button>
              </div>
              {exActive.length === 0 ? (
                <div style={{ padding: '12px 0', textAlign: 'center', color: INK4, fontSize: 13 }}>暂无进行中或即将开拍的场次</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(exActive.length, 3)}, 1fr)`, gap: 12 }}>
                  {exActive.map(sess => (
                    <SessionCard
                      key={sess.id}
                      session={sess}
                      onSelect={(s) => {
                        setSelectedSession(s);
                        setActiveHouse(s.house_id || pcExpandedHouseId);
                        setPage(1);
                        setPcExpandedHouseId(null);
                      }}
                      allClosedFn={allClosed}
                      isSessionLiveFn={isSessionLive}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* ── Selected session banner ── */}
        {selectedSession && (() => {
          const sessEnded = allClosed(selectedSession);
          const st = sessEnded ? STATUS_MAP.closed : (STATUS_MAP[isSessionLive(selectedSession) ? 'live' : selectedSession.status] || STATUS_MAP.upcoming);
          return (
            <div style={{
              flexShrink: 0, background: 'rgba(184,137,42,.05)', borderBottom: `1px solid rgba(184,137,42,.18)`,
              padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: INK4, flexShrink: 0 }}>{selectedSession.house_name}</span>
              <span style={{ color: INK5, fontSize: 12 }}>›</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: INK, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedSession.name}</span>
              <span style={{ fontSize: 11, color: INK3, flexShrink: 0 }}>
                {selectedSession.end_at ? `${t('browse.deadline')} ${fmtCST(selectedSession.end_at, 'MM/DD HH:mm')}` : ''}
              </span>
              <span style={{ fontSize: 11, color: INK4, fontFamily: 'var(--mono)', flexShrink: 0 }}>{t('browse.itemsCount', { count: selectedSession.lot_count || 0 })}</span>
              <button
                onClick={() => { setSelectedSession(null); setActiveHouse(null); setPage(1); }}
                style={{ width: 22, height: 22, border: 'none', background: 'rgba(0,0,0,.06)', borderRadius: '50%', cursor: 'pointer', fontSize: 12, color: INK3, padding: 0, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >✕</button>
            </div>
          );
        })()}

        {/* ── 以图搜索结果条 (Desktop) ── */}
        {(aiSearching || aiMeta) && (
          <AiImageResultStrip meta={aiMeta} searching={aiSearching}
            onClear={() => { setAiResults(null); setAiMeta(null); }} />
        )}

        {/* ── AI Recommendations (Desktop) ── */}
        {!keyword && !aiResults && !category && (
          <RecommendSection
            jpyCnyRate={jpyCnyRate}
            onLotClick={(lot) => setDialogLot({ id: lot.id, titleJa: lot.title_ja || lot.title || '' })}
          />
        )}

        {/* ── Search + Toolbar ── */}
        <div style={{
          flexShrink: 0, padding: '8px 20px', borderBottom: `1px solid ${BD2}`,
          display: 'flex', alignItems: 'center', gap: 8, background: '#fff',
        }}>
          {/* Search input */}
          <div style={{ position: 'relative', width: 220, flexShrink: 0 }}>
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onFocus={() => { setSearchHistory(loadHistory()); setSearchFocused(true); }}
              onBlur={() => { setTimeout(() => setSearchFocused(false), 150); }}
              onKeyDown={e => { if (e.key === 'Enter') doSearch(searchInput); }}
              placeholder={t('browse.searchPlaceholder')}
              style={{
                width: '100%', padding: '6px 32px 6px 28px', border: `1px solid ${BD}`,
                borderRadius: 4, fontSize: 12, color: INK, background: '#fff', outline: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='%23A8998A' stroke-width='1.8'%3E%3Ccircle cx='9' cy='9' r='5.5'/%3E%3Cline x1='13.5' y1='13.5' x2='17' y2='17'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat', backgroundPosition: '7px center', backgroundSize: 14,
              }}
            />
            {searchFocused && !keyword && !aiResults && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, width: 340, zIndex: 50, borderRadius: 8, boxShadow: '0 8px 28px rgba(26,20,16,.16)', border: `1px solid ${BD2}`, overflow: 'hidden' }}>
                <HistoryHotPanel
                  history={searchHistory} hot={hotKeywords}
                  onPick={doSearch}
                  onClearHistory={() => { try { localStorage.removeItem(LS_HISTORY); } catch {} setSearchHistory([]); }}
                />
              </div>
            )}
            {/* 以图搜物：相机图标放搜索框右侧 */}
            <ImageSearchButton
              onResults={(results, meta) => { setAiResults(results); setAiMeta(meta); }}
              onLoading={setAiSearching}
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          </div>
          {keyword && (
            <button onClick={() => { setSearchInput(''); resetAndFetch(setKeyword, ''); }}
              style={{ width: 22, height: 22, border: `1px solid ${BD}`, borderRadius: '50%', background: BG2, cursor: 'pointer', fontSize: 11, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}
            >✕</button>
          )}
          <div style={{ width: 1, height: 16, background: BD2, flexShrink: 0 }} />
          {/* Category tabs */}
          <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {CATEGORIES.map(cat => (
              <button key={cat.value} onClick={() => { if (cat.value !== category) resetAndFetch(setCategory, cat.value); }}
                style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  color: category === cat.value ? '#fff' : INK3,
                  background: category === cat.value ? INK : 'transparent',
                  border: '1px solid transparent', transition: 'all .12s', whiteSpace: 'nowrap',
                }}>
                {t(cat.labelKey)}
              </button>
            ))}
          </div>
          <div style={{ width: 1, height: 16, background: BD2, flexShrink: 0 }} />
          {/* Brand filter button */}
          <div style={{ position: 'relative' }}>
            <button
              ref={brandBtnRef}
              onClick={() => setBrandPopoverOpen(v => !v)}
              style={{
                padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                color: selectedBrands.size > 0 ? '#fff' : INK3,
                background: selectedBrands.size > 0 ? GOLD : 'transparent',
                border: `1px solid ${selectedBrands.size > 0 ? GOLD : BD}`,
                transition: 'all .12s', whiteSpace: 'nowrap',
              }}>
              🏷 {t('browse.filterBrand')}{selectedBrands.size > 0 ? ` (${selectedBrands.size})` : ''}
            </button>
            {/* Brand Popover */}
            {brandPopoverOpen && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setBrandPopoverOpen(false)} />
                <div style={{
                  position: 'absolute', top: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)',
                  background: '#fff', border: `1px solid ${BD}`, borderRadius: 8, boxShadow: '0 8px 32px rgba(26,20,16,.13)',
                  zIndex: 100, width: 280, maxHeight: 360, display: 'flex', flexDirection: 'column',
                }}>
                  <div style={{ position: 'absolute', top: -7, left: '50%', width: 12, height: 12, background: '#fff', border: `1px solid ${BD}`, borderBottom: 'none', borderRight: 'none', transform: 'translateX(-50%) rotate(45deg)' }} />
                  <div style={{ padding: '10px 14px 8px', borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>{t('browse.filterBrand')}</span>
                    {selectedBrands.size > 0 && (
                      <button onClick={() => { setSelectedBrands(new Set()); setPage(1); }} style={{ fontSize: 11, color: INK4, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>{t('browse.clearAll')}</button>
                    )}
                  </div>
                  <div style={{ overflowY: 'auto', padding: '6px 8px' }}>
                    {brandList.length > 0 ? brandList.map(({ brand, count }) => (
                      <label key={brand} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 6px', borderRadius: 4, cursor: 'pointer', background: selectedBrands.has(brand) ? 'rgba(184,137,42,.08)' : 'transparent', transition: 'background .1s' }}>
                        <input type="checkbox" checked={selectedBrands.has(brand)} onChange={() => { setSelectedBrands(prev => { const next = new Set(prev); next.has(brand) ? next.delete(brand) : next.add(brand); return next; }); setPage(1); }} style={{ accentColor: GOLD, width: 13, height: 13, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, color: INK, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brand}</span>
                        <span style={{ fontSize: 10, color: INK4, fontFamily: 'var(--mono,monospace)', flexShrink: 0 }}>{count}</span>
                      </label>
                    )) : (
                      <div style={{ padding: '14px 6px', textAlign: 'center', color: INK4, fontSize: 12 }}>{t('browse.noBrandData')}</div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
          <div style={{ width: 1, height: 16, background: BD2, flexShrink: 0 }} />
          {/* 高级筛选按钮 */}
          {(() => {
            const filterCount = [
              appliedFilter.priceMin || appliedFilter.priceMax,
              appliedFilter.startMin || appliedFilter.startMax,
              appliedFilter.buynowMin || appliedFilter.buynowMax,
              appliedFilter.categories?.length > 0,
              appliedFilter.grades?.length > 0,
              appliedFilter.brands?.length > 0,
              appliedFilter._session,
            ].filter(Boolean).length;
            const hasFilter = filterCount > 0;
            return (
              <button onClick={() => setFilterSheetOpen(true)} style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                borderRadius: 20, fontSize: 11, cursor: 'pointer',
                color: hasFilter ? GOLD : INK3, background: hasFilter ? 'rgba(184,137,42,.1)' : 'transparent',
                border: `1px solid ${hasFilter ? GOLD : BD}`, transition: 'all .12s', whiteSpace: 'nowrap',
              }}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <line x1="1" y1="3" x2="11" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="2.5" y1="6" x2="9.5" y2="6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="4" y1="9" x2="8" y2="9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                {t('browse.filter')}{hasFilter ? `(${filterCount})` : ''}
              </button>
            );
          })()}
          <div style={{ width: 1, height: 16, background: BD2, flexShrink: 0 }} />
          {/* Sort */}
          <select value={sort} onChange={e => resetAndFetch(setSort, e.target.value)}
            style={{ padding: '4px 8px', border: `1px solid ${BD}`, borderRadius: 4, fontSize: 11, color: INK3, background: '#fff', outline: 'none', cursor: 'pointer' }}
          >
            <option value="box_no">{t('browse.sortBoxNo')}</option>
            <option value="end_asc">{t('browse.sortEndingSoon')}</option>
            <option value="bid_desc">{t('browse.sortPopular')}</option>
            <option value="price_desc">{t('browse.sortPriceDesc')}</option>
            <option value="price_asc">{t('browse.sortPriceAsc')}</option>
            <option value="newest">{t('browse.sortNewest')}</option>
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: INK4, fontFamily: 'var(--mono,monospace)' }}>
            {t('browse.itemsCount', { count: total.toLocaleString() })}
          </span>
          {/* Mini pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={{
                width: 22, height: 22, border: `1px solid ${page === 1 ? BD2 : BD}`, borderRadius: 3,
                background: '#fff', color: page === 1 ? INK5 : INK3, fontSize: 12,
                cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}>‹</button>
              <span style={{ fontFamily: 'var(--mono,monospace)', fontSize: 11, color: INK3, minWidth: 54, textAlign: 'center' }}>
                {page} / {totalPages}
              </span>
              <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={{
                width: 22, height: 22, border: `1px solid ${page === totalPages ? BD2 : BD}`, borderRadius: 3,
                background: '#fff', color: page === totalPages ? INK5 : INK3, fontSize: 12,
                cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}>›</button>
            </div>
          )}
          {/* View toggle */}
          <div style={{ display: 'flex', gap: 2 }}>
            <button onClick={() => setIsListView(false)} style={{
              width: 26, height: 26, border: `1px solid ${BD}`, borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12,
              background: !isListView ? INK : 'transparent', color: !isListView ? '#fff' : INK4, transition: 'all .12s',
            }}>⊞</button>
            <button onClick={() => setIsListView(true)} style={{
              width: 26, height: 26, border: `1px solid ${BD}`, borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12,
              background: isListView ? INK : 'transparent', color: isListView ? '#fff' : INK4, transition: 'all .12s',
            }}>☰</button>
          </div>
        </div>

        {/* ── Main Content Area (full width, scrollable) ── */}
        <div ref={mainRef} style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', background: BG }}>
          {!selectedSession && !activeHouse && !hasActiveQuery ? (
            /* ── View: Home — show live + preview sessions（未搜索时才显示会场首页） ── */
            <div style={{ padding: '24px 28px 40px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
                {[
                  { v: houses.length || '--', l: t('browse.partnerHouses') },
                  { v: total || '--', l: t('browse.currentLots') },
                  { v: liveSessions.length, l: t('browse.liveSessions') },
                  { v: '98.2%', l: t('browse.bidSuccessRate') },
                ].map(s => (
                  <div key={s.l} style={{ background: '#fff', border: `1px solid ${BD2}`, borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontFamily: 'var(--mono,monospace)', fontSize: 18, fontWeight: 500, color: INK, marginBottom: 2 }}>{s.v}</div>
                    <div style={{ fontSize: 10, color: INK4 }}>{s.l}</div>
                  </div>
                ))}
              </div>
              {/* 场次卡片列表 */}
              {[...liveSessions, ...previewSessions].length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))', gap: 14 }}>
                  {[...liveSessions, ...previewSessions].map(s => (
                    <SessionCard
                      key={s.id}
                      session={s}
                      onSelect={(sess) => { if (selectedSession?.id === sess.id) return; setActiveHouse(sess.house_id || sess.house_name); setSelectedSession(sess); setPage(1); }}
                      allClosedFn={allClosed}
                      isSessionLiveFn={isSessionLive}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ padding: '60px 20px', textAlign: 'center', color: INK4 }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
                  <div style={{ fontSize: 14, color: INK3, marginBottom: 6 }}>{t('browse.noResults')}</div>
                  <div style={{ fontSize: 12, color: INK4 }}>{t('browse.selectAuctionHouse')}</div>
                </div>
              )}
            </div>
          ) : (
            /* ── View: Session Lots ── */
            <div style={{ padding: '16px 28px 40px' }}>
              <Spin spinning={loading && page === 1}>
                {lots.length === 0 && !loading ? (
                  selectedSession && isSessionNotStarted(selectedSession) ? (
                    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                      <div style={{ fontSize: 52, marginBottom: 14 }}>⏳</div>
                      <div style={{ fontSize: 17, fontWeight: 600, color: '#5A4A3A', marginBottom: 10, letterSpacing: 1 }}>{t('browse.sessionNotStarted')}</div>
                      <div style={{ fontSize: 14, color: INK3, lineHeight: 1.8 }}>
                        {t('browse.sessionScheduledAt')} <span style={{ fontWeight: 600, color: GOLD, margin: '0 4px' }}>{fmtStartShort(selectedSession) || '—'}</span> {t('browse.sessionStart')}
                      </div>
                      <div style={{ fontSize: 13, color: INK4, marginTop: 8 }}>{t('browse.catalogWillPublish')}</div>
                    </div>
                  ) : hasActiveQuery && !selectedSession ? (
                    /* 搜了/筛了但0结果 → 明确"没找到" */
                    <NoSearchResult keyword={keyword}
                      onClear={() => { setSearchInput(''); setCategory(''); setAiResults(null); setAiMeta(null); setAppliedFilter(EMPTY_FILTER); resetAndFetch(setKeyword, ''); }} />
                  ) : (
                    <div style={{ padding: '80px 20px', textAlign: 'center' }}>
                      <svg width="90" height="90" viewBox="0 0 90 90" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16, opacity: 0.55 }}>
                        <circle cx="38" cy="38" r="22" stroke="#B8892A" strokeWidth="4" fill="none" />
                        <line x1="54" y1="54" x2="74" y2="74" stroke="#B8892A" strokeWidth="5" strokeLinecap="round" />
                        <polygon points="38,24 48,34 38,50 28,34" stroke="#B8892A" strokeWidth="2" fill="rgba(184,137,42,0.08)" strokeLinejoin="round" />
                        <line x1="28" y1="34" x2="48" y2="34" stroke="#B8892A" strokeWidth="1.5" opacity="0.5" />
                        <circle cx="68" cy="22" r="10" fill="#EDE8E0" stroke="#C8B89A" strokeWidth="1.5" />
                        <line x1="64" y1="18" x2="72" y2="26" stroke="#9B2335" strokeWidth="2" strokeLinecap="round" />
                        <line x1="72" y1="18" x2="64" y2="26" stroke="#9B2335" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <div style={{ fontSize: 15, fontWeight: 600, color: '#5A4A3A', marginBottom: 6, letterSpacing: 1 }}>{t('browse.noFeaturedLots')}</div>
                      {(() => {
                        const house = houses.find(h => h.id === selectedSession?.house_id);
                        const _sc = house?.supported_categories; // 可能是数组(JSON 列)或逗号分隔字符串
                        const cats = (Array.isArray(_sc) ? _sc : String(_sc || '').split(',')).map(s => String(s).trim()).filter(Boolean);
                        const labels = cats.map(c => { const f = CATEGORIES.find(x => x.value === c); return f ? t(f.labelKey) : c; });
                        return labels.length > 0
                          ? <div style={{ fontSize: 13, color: '#8A7A6A', lineHeight: 1.7 }}>{t('browse.noLotsForCategories', { categories: labels.join('、') })}</div>
                          : <div style={{ fontSize: 13, color: '#8A7A6A', lineHeight: 1.7 }}>{t('browse.noMatchingLots')}</div>;
                      })()}
                    </div>
                  )
                ) : (
                  <>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isListView ? '1fr' : 'repeat(auto-fill,minmax(195px,1fr))',
                      gap: isListView ? 6 : 11,
                    }}>
                      {sortedLots.map(lot => {
                        const isWatchlisted = watchlistLoaded ? watchlistedIds.has(lot.id) : (watchlistedIds.has(lot.id) || !!lot.is_watchlisted);
                        return <LotCard key={lot.id} lot={lot} isWatchlisted={isWatchlisted} isBidding={biddingIds.has(lot.id) || !!lot.has_active_mandate} onToggleWatchlist={toggleWatchlist} onOpenDetail={lot => setDialogLot({ id: lot.id, titleJa: lot.title_ja || lot.title || '' })} jpyCnyRate={jpyCnyRate} />;
                      })}
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '28px 0 8px', userSelect: 'none' }}>
                        <button onClick={() => goToPage(page - 1)} disabled={page === 1} style={{
                          width: 32, height: 32, border: `1px solid ${page === 1 ? BD2 : BD}`, borderRadius: 4,
                          background: '#fff', color: page === 1 ? INK5 : INK3, fontSize: 14,
                          cursor: page === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all .15s', fontFamily: 'var(--mono)',
                        }}
                          onMouseEnter={e => { if (page !== 1) { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; } }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = page === 1 ? BD2 : BD; e.currentTarget.style.color = page === 1 ? INK5 : INK3; }}
                        >‹</button>
                        {buildPageNums().map((p, i) =>
                          p === '…' ? (
                            <span key={`e${i}`} style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', color: INK4, fontSize: 12, fontFamily: 'var(--mono)' }}>…</span>
                          ) : (
                            <button key={p} onClick={() => goToPage(p)} style={{
                              width: 32, height: 32, border: `1px solid ${p === page ? GOLD : BD}`, borderRadius: 4,
                              background: p === page ? GOLD : '#fff', color: p === page ? '#fff' : INK3,
                              fontSize: 12, cursor: 'pointer', fontFamily: 'var(--mono)', fontWeight: p === page ? 600 : 400,
                              transition: 'all .15s', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}
                              onMouseEnter={e => { if (p !== page) { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; } }}
                              onMouseLeave={e => { if (p !== page) { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = INK3; } }}
                            >{p}</button>
                          )
                        )}
                        <button onClick={() => goToPage(page + 1)} disabled={page === totalPages} style={{
                          width: 32, height: 32, border: `1px solid ${page === totalPages ? BD2 : BD}`, borderRadius: 4,
                          background: '#fff', color: page === totalPages ? INK5 : INK3, fontSize: 14,
                          cursor: page === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all .15s', fontFamily: 'var(--mono)',
                        }}
                          onMouseEnter={e => { if (page !== totalPages) { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; } }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = page === totalPages ? BD2 : BD; e.currentTarget.style.color = page === totalPages ? INK5 : INK3; }}
                        >›</button>
                        <span style={{ marginLeft: 10, fontFamily: 'var(--mono)', fontSize: 11, color: INK4 }}>
                          {t('browse.paginationInfo', { start: (page - 1) * pageSize + 1, end: Math.min(page * pageSize, total), total: total.toLocaleString() })}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Spin>
            </div>
          )}
        </div>

        {/* ── House Session Dropdown Popup (shared with mobile) ── */}
        {houseSheetOpen && (() => {
          const sheetHouse = houses.find(h => (h.id || h.name) === sheetHouseId);
          if (!sheetHouse) return null;
          const sheetSessions = sessionsByHouse[sheetHouse.id || sheetHouse.name] || sessionsByHouse[sheetHouse.name] || [];
          const POP_W = 320;
          const ARROW_H = 10;
          const margin = 8;
          const screenW = window.innerWidth;
          const rawLeft = sheetAnchor.x - POP_W / 2;
          const left = Math.max(margin, Math.min(rawLeft, screenW - POP_W - margin));
          const arrowCx = Math.max(14, Math.min(sheetAnchor.x - left, POP_W - 14));
          return (
            <>
              <div onClick={() => setHouseSheetOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 299 }} />
              <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: sheetAnchor.y, left, width: POP_W, maxHeight: '60vh', zIndex: 300, display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'absolute', top: -(ARROW_H + 1), left: arrowCx - (ARROW_H + 1), width: 0, height: 0, borderLeft: `${ARROW_H + 1}px solid transparent`, borderRight: `${ARROW_H + 1}px solid transparent`, borderBottom: `${ARROW_H + 1}px solid ${BD}`, zIndex: 1 }} />
                <div style={{ position: 'absolute', top: -ARROW_H, left: arrowCx - ARROW_H, width: 0, height: 0, borderLeft: `${ARROW_H}px solid transparent`, borderRight: `${ARROW_H}px solid transparent`, borderBottom: `${ARROW_H}px solid #fff`, zIndex: 2 }} />
                <div style={{ background: '#fff', border: `1px solid ${BD}`, borderRadius: 10, boxShadow: '0 8px 32px rgba(26,20,16,.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxHeight: '60vh' }}>
                  <div style={{ padding: '11px 14px 10px', borderBottom: `1px solid ${BD2}`, display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0 }}>
                    <div style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0, background: sheetHouse.logo_url ? '#fff' : (sheetHouse.color || '#374151'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, overflow: 'hidden' }}>
                      {sheetHouse.logo_url ? <img src={sheetHouse.logo_url} alt="" style={{ width: '88%', height: '88%', objectFit: 'contain' }} /> : (sheetHouse.emoji || '🏢')}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: INK, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sheetHouse.name_zh || sheetHouse.name}</div>
                      {sheetHouse.name_ja && <div style={{ fontSize: 10, color: INK4 }}>{sheetHouse.name_ja}</div>}
                    </div>
                    <button onClick={() => setHouseSheetOpen(false)} style={{ width: 24, height: 24, border: `1px solid ${BD}`, borderRadius: 5, background: BG2, cursor: 'pointer', fontSize: 13, color: INK3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0 }}>✕</button>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '3px 0 4px' }}>
                    {sheetSessions.length === 0 && <div style={{ padding: '20px 14px', textAlign: 'center', color: INK4, fontSize: 12 }}>{t('browse.noSessions')}</div>}
                    {sheetSessions.map(sess => {
                      const sessEnded = allClosed(sess);
                      const effectiveStatus = isSessionLive(sess) ? 'live' : sess.status;
                      const st = sessEnded ? STATUS_MAP.closed : (STATUS_MAP[effectiveStatus] || STATUS_MAP.upcoming);
                      const isSel = selectedSession?.id === sess.id;
                      return (
                        <div key={sess.id} onClick={() => { setSelectedSession(sess); setActiveHouse(sess.house_id || sheetHouse.id || sheetHouse.name); setPage(1); setHouseSheetOpen(false); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', cursor: 'pointer', background: isSel ? 'rgba(184,137,42,.06)' : 'transparent', borderLeft: isSel ? `3px solid ${GOLD}` : '3px solid transparent', borderBottom: `1px solid ${BD3}`, transition: 'background .1s' }}
                        >
                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot, flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: isSel ? 600 : 500, color: sessEnded ? INK4 : INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sessEnded && <span style={{ marginRight: 2, opacity: .7, fontSize: 11 }}>{t('browse.sessionEnded')}</span>}{sess.name}
                            </div>
                            <div style={{ fontSize: 10, color: INK4, marginTop: 1 }}>
                              <span style={{ padding: '1px 4px', borderRadius: 3, background: st.bg, color: st.color, fontSize: 9, fontWeight: 600 }}>{t(st.labelKey)}</span>
                              {sess.end_at && <span style={{ marginLeft: 3 }}>{t('browse.deadline')} {fmtCST(sess.end_at, 'MM/DD HH:mm')}</span>}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 500, color: INK }}>{sess.lot_count || 0}</div>
                            <div style={{ fontSize: 9, color: INK4 }}>{t('browse.pieces')}</div>
                          </div>
                          {isSel && <span style={{ fontSize: 14, color: GOLD, flexShrink: 0 }}>✓</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    )} {/* end desktop layout */}

      <style>{`@keyframes blink{0%,100%{opacity:1}50%{opacity:.2}}`}</style>

      {/* FilterSheet — PC版（portal render，覆盖整个视口） */}
      {!isMobile && (
        <FilterSheet
          open={filterSheetOpen}
          onClose={() => setFilterSheetOpen(false)}
          sessions={selectedSession ? (sessionsByHouse[selectedSession.house_id] || sessionsByHouse[selectedSession.house_name] || []) : activeHouse ? (sessionsByHouse[activeHouse] || []) : sessions}
          brandList={brandList}
          initialFilter={appliedFilter}
          appliedSession={appliedFilter._session || null}
          onApply={(newFilter, newSession) => {
            setAppliedFilter({ ...newFilter, _session: newSession || null });
            if (newSession) {
              setSelectedSession(newSession);
              setActiveHouse(newSession.house_id || null);
            }
            setPage(1);
          }}
        />
      )}

      {/* Lot Detail Dialog */}
      {dialogLot && (
        <LotDetailDialog
          lotId={dialogLot.id}
          titleJa={dialogLot.titleJa}
          onClose={() => window.history.back()}
          onBidPlaced={(lotId) => {
            // Immediately show 竞拍中 badge without waiting for next API fetch
            setBiddingIds(prev => new Set([...prev, lotId]));
            setLots(prev => prev.map(l => l.id === lotId ? { ...l, has_active_mandate: 1 } : l));
          }}
        />
      )}
    </div>
  );
}
